import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { 
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { format, addDays, parseISO, differenceInCalendarDays } from 'date-fns';
import { sampleEmployees, sampleClients, sampleProperties } from '../utils/sampleData';
import { isCustomEstimate as checkCustomEstimate } from '../utils/helpers';
import { sampleEstimates } from '../utils/estimateData';
import {
  defaultSalesAnalyticsFields,
  appendStageEntry,
  deriveQuoteAmount,
  inferLeadStatus,
  seedStageHistory,
} from '../utils/leadStageHistory';
import {
  affectedDayKeysForLead,
  computeDailyMetrics,
} from '../utils/salesMetricsAggregation';
import { sampleJobs } from '../utils/jobData';
import { sampleInvoices, createInvoiceFromJob, generateInvoiceNumber } from '../utils/invoiceData';

// Collection names
const COLLECTIONS = {
  clients: 'clients',
  employees: 'employees',
  properties: 'properties',
  estimates: 'estimates',
  jobs: 'jobs',
  invoices: 'invoices',
  messages: 'messages',
  shifts: 'shifts',
  timeOffRequests: 'timeOffRequests',
  internalMessages: 'internalMessages',
  dispatchLog: 'dispatchLog',
  todos: 'todos',
  reviews: 'reviews',
  communicationLogs: 'communicationLogs',
  quizzes: 'quizzes',
  quizAttempts: 'quizAttempts',
  employeeDocuments: 'employeeDocuments',
  employeeConversations: 'employeeConversations',
  clientDocuments: 'clientDocuments',
  attendancePoints: 'attendancePoints',
  complaints: 'complaints',
  feedbacks: 'feedbacks',
  expenses: 'expenses',
  deposits: 'deposits',
  accounts: 'accounts',
  expenseCategories: 'expenseCategories',
  transfers: 'transfers',
  dayNotes: 'dayNotes'
};

// Collections that exist in Firestore but aren't synced into local state.
// Dashboards read these on demand instead of subscribing to every doc.
const SALES_METRICS_COLLECTION = 'salesMetrics';

// Initial state
const initialState = {
  employees: [],
  clients: [],
  properties: [],
  estimates: [],
  jobs: [],
  invoices: [],
  messages: [],
  shifts: [],
  timeOffRequests: [],
  internalMessages: [],
  dispatchLog: [],
  todos: [],
  reviews: [],
  communicationLogs: [],
  quizzes: [],
  quizAttempts: [],
  employeeDocuments: [],
  employeeConversations: [],
  clientDocuments: [],
  attendancePoints: [],
  complaints: [],
  feedbacks: [],
  expenses: [],
  deposits: [],
  accounts: [],
  expenseCategories: [],
  transfers: [],
  dayNotes: [],
  loading: true,
  error: null,
  initialized: false
};

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_INITIALIZED':
      return { ...state, initialized: true, loading: false };
    
    case 'LOAD_DATA':
      return { 
        ...state, 
        ...action.payload,
        loading: false,
        initialized: true 
      };

    // ===== CLIENTS =====
    case 'SET_CLIENTS':
      return { ...state, clients: action.payload };
    case 'ADD_CLIENT':
      return { ...state, clients: [...state.clients, action.payload] };
    case 'UPDATE_CLIENT':
      return {
        ...state,
        clients: state.clients.map(c => 
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        )
      };
    case 'DELETE_CLIENT':
      return { ...state, clients: state.clients.filter(c => c.id !== action.payload) };
    
    // ===== EMPLOYEES =====
    case 'SET_EMPLOYEES':
      return { ...state, employees: action.payload };
    case 'ADD_EMPLOYEE':
      return { ...state, employees: [...state.employees, action.payload] };
    case 'UPDATE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.map(e => 
          e.id === action.payload.id ? { ...e, ...action.payload } : e
        )
      };
    case 'DELETE_EMPLOYEE':
      return { ...state, employees: state.employees.filter(e => e.id !== action.payload) };
    
    // ===== PROPERTIES =====
    case 'SET_PROPERTIES':
      return { ...state, properties: action.payload };
    case 'ADD_PROPERTY':
      return { ...state, properties: [...state.properties, action.payload] };
    case 'UPDATE_PROPERTY':
      return {
        ...state,
        properties: state.properties.map(p => 
          p.id === action.payload.id ? { ...p, ...action.payload } : p
        )
      };
    case 'DELETE_PROPERTY':
      return { ...state, properties: state.properties.filter(p => p.id !== action.payload) };

    // ===== ESTIMATES =====
    case 'SET_ESTIMATES':
      return { ...state, estimates: action.payload };
    case 'ADD_ESTIMATE':
      return { ...state, estimates: [...state.estimates, action.payload] };
    case 'UPDATE_ESTIMATE':
      return {
        ...state,
        estimates: state.estimates.map(e => 
          e.id === action.payload.id ? { ...e, ...action.payload } : e
        )
      };
    case 'DELETE_ESTIMATE':
      return { ...state, estimates: state.estimates.filter(e => e.id !== action.payload) };
    
    case 'SEND_ESTIMATE':
      return {
        ...state,
        estimates: state.estimates.map(e => 
          e.id === action.payload.id 
            ? { 
                ...e, 
                status: 'sent',
                sentAt: new Date().toISOString(),
                sentVia: action.payload.sentVia || 'email',
                approvalToken: action.payload.approvalToken,
                validUntil: action.payload.validUntil,
                updatedAt: new Date().toISOString()
              } 
            : e
        )
      };
    
    case 'APPROVE_ESTIMATE':
      return {
        ...state,
        estimates: state.estimates.map(e => 
          e.id === action.payload.id 
            ? { 
                ...e, 
                status: 'approved',
                approvedAt: new Date().toISOString(),
                lineItems: action.payload.approvedLineItems || e.lineItems,
                approvedRecurringTotal: action.payload.approvedRecurringTotal,
                approvedDeepCleanTotal: action.payload.approvedDeepCleanTotal,
                clientNotes: action.payload.clientNotes || '',
                updatedAt: new Date().toISOString()
              } 
            : e
        )
      };
    
    case 'DECLINE_ESTIMATE':
      return {
        ...state,
        estimates: state.estimates.map(e => 
          e.id === action.payload.id 
            ? { 
                ...e, 
                status: 'declined',
                declinedAt: new Date().toISOString(),
                declineReason: action.payload.reason || '',
                updatedAt: new Date().toISOString()
              } 
            : e
        )
      };
    
    case 'EXPIRE_ESTIMATE':
      return {
        ...state,
        estimates: state.estimates.map(e => 
          e.id === action.payload.id 
            ? { ...e, status: 'expired', updatedAt: new Date().toISOString() } 
            : e
        )
      };
    
    case 'CONVERT_ESTIMATE_TO_JOB':
      return {
        ...state,
        estimates: state.estimates.map(e => 
          e.id === action.payload.estimateId 
            ? { 
                ...e, 
                status: 'converted',
                convertedToJobId: action.payload.jobId,
                convertedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              } 
            : e
        ),
        jobs: [...state.jobs, action.payload.job]
      };

    // ===== JOBS =====
    case 'SET_JOBS':
      return { ...state, jobs: action.payload };
    case 'ADD_JOB':
      return { ...state, jobs: [...state.jobs, action.payload] };
    case 'UPDATE_JOB':
      return {
        ...state,
        jobs: state.jobs.map(j => 
          j.id === action.payload.id ? { ...j, ...action.payload } : j
        )
      };
    case 'DELETE_JOB':
      return { ...state, jobs: state.jobs.filter(j => j.id !== action.payload) };

    // ===== INVOICES =====
    case 'SET_INVOICES':
      return { ...state, invoices: action.payload };
    case 'ADD_INVOICE':
      // Prevent duplicates - only add if not already present
      if (state.invoices.some(i => i.id === action.payload.id)) {
        return state;
      }
      return { ...state, invoices: [...state.invoices, action.payload] };
    case 'UPDATE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.map(i => 
          i.id === action.payload.id ? { ...i, ...action.payload } : i
        )
      };

    // ===== MESSAGES =====
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map(m => 
          m.id === action.payload.id ? { ...m, ...action.payload } : m
        )
      };
    case 'DELETE_MESSAGE':
      return { ...state, messages: state.messages.filter(m => m.id !== action.payload) };

    // ===== SHIFTS =====
    case 'SET_SHIFTS':
      return { ...state, shifts: action.payload };
    case 'ADD_SHIFT':
      return { ...state, shifts: [...state.shifts, action.payload] };
    case 'UPDATE_SHIFT':
      return {
        ...state,
        shifts: state.shifts.map(s => 
          s.id === action.payload.id ? { ...s, ...action.payload } : s
        )
      };
    case 'DELETE_SHIFT':
      return { ...state, shifts: state.shifts.filter(s => s.id !== action.payload) };

    // ===== TIME OFF REQUESTS =====
    case 'SET_TIMEOFFREQUESTS':
      return { ...state, timeOffRequests: action.payload };
    case 'ADD_TIMEOFF_REQUEST':
      return { ...state, timeOffRequests: [...state.timeOffRequests, action.payload] };
    case 'UPDATE_TIMEOFF_REQUEST':
      return {
        ...state,
        timeOffRequests: state.timeOffRequests.map(t => 
          t.id === action.payload.id ? { ...t, ...action.payload } : t
        )
      };
    case 'DELETE_TIMEOFF_REQUEST':
      return { ...state, timeOffRequests: state.timeOffRequests.filter(t => t.id !== action.payload) };

    // ===== INTERNAL MESSAGES =====
    case 'SET_INTERNALMESSAGES':
      return { ...state, internalMessages: action.payload };
    case 'ADD_INTERNAL_MESSAGE':
      return { ...state, internalMessages: [...state.internalMessages, action.payload] };
    case 'UPDATE_INTERNAL_MESSAGE':
      return {
        ...state,
        internalMessages: state.internalMessages.map(m => 
          m.id === action.payload.id ? { ...m, ...action.payload } : m
        )
      };
    case 'DELETE_INTERNAL_MESSAGE':
      return { ...state, internalMessages: state.internalMessages.filter(m => m.id !== action.payload) };
    
    // ===== DISPATCH LOG =====
    case 'SET_DISPATCHLOG':
      return { ...state, dispatchLog: action.payload };

    // ===== TODOS =====
    case 'SET_TODOS':
      return { ...state, todos: action.payload };
    case 'ADD_TODO':
      return { ...state, todos: [...state.todos, action.payload] };
    case 'UPDATE_TODO':
      return {
        ...state,
        todos: state.todos.map(t => 
          t.id === action.payload.id ? { ...t, ...action.payload } : t
        )
      };
    case 'DELETE_TODO':
      return { ...state, todos: state.todos.filter(t => t.id !== action.payload) };

    // ===== COMMUNICATION LOGS =====
    case 'SET_COMMUNICATIONLOGS':
      return { ...state, communicationLogs: action.payload };

    // ===== REVIEWS =====
    case 'SET_REVIEWS':
      return { ...state, reviews: action.payload };
    case 'ADD_REVIEW':
      return { ...state, reviews: [...state.reviews, action.payload] };
    case 'UPDATE_REVIEW':
      return {
        ...state,
        reviews: state.reviews.map(r => 
          r.id === action.payload.id ? { ...r, ...action.payload } : r
        )
      };
    
    // ===== QUIZZES =====
    case 'SET_QUIZZES':
      return { ...state, quizzes: action.payload };
    case 'ADD_QUIZ':
      return { ...state, quizzes: [...state.quizzes, action.payload] };
    case 'UPDATE_QUIZ':
      return {
        ...state,
        quizzes: state.quizzes.map(q =>
          q.id === action.payload.id ? { ...q, ...action.payload } : q
        )
      };
    case 'DELETE_QUIZ':
      return { ...state, quizzes: state.quizzes.filter(q => q.id !== action.payload) };

    // ===== QUIZ ATTEMPTS =====
    case 'SET_QUIZATTEMPTS':
      return { ...state, quizAttempts: action.payload };
    case 'ADD_QUIZATTEMPT':
      return { ...state, quizAttempts: [...state.quizAttempts, action.payload] };

    // ===== EMPLOYEE DOCUMENTS =====
    case 'SET_EMPLOYEEDOCUMENTS':
      return { ...state, employeeDocuments: action.payload };
    case 'ADD_EMPLOYEEDOCUMENT':
      return { ...state, employeeDocuments: [...state.employeeDocuments, action.payload] };
    case 'DELETE_EMPLOYEEDOCUMENT':
      return { ...state, employeeDocuments: state.employeeDocuments.filter(d => d.id !== action.payload) };

    // ===== EMPLOYEE CONVERSATIONS =====
    case 'SET_EMPLOYEECONVERSATIONS':
      return { ...state, employeeConversations: action.payload };
    case 'ADD_EMPLOYEECONVERSATION':
      return { ...state, employeeConversations: [...state.employeeConversations, action.payload] };
    case 'UPDATE_EMPLOYEECONVERSATION':
      return {
        ...state,
        employeeConversations: state.employeeConversations.map(c =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        )
      };
    case 'DELETE_EMPLOYEECONVERSATION':
      return { ...state, employeeConversations: state.employeeConversations.filter(c => c.id !== action.payload) };

    // ===== CLIENT DOCUMENTS =====
    case 'SET_CLIENTDOCUMENTS':
      return { ...state, clientDocuments: action.payload };
    case 'ADD_CLIENTDOCUMENT':
      return { ...state, clientDocuments: [...state.clientDocuments, action.payload] };
    case 'DELETE_CLIENTDOCUMENT':
      return { ...state, clientDocuments: state.clientDocuments.filter(d => d.id !== action.payload) };

    // ===== ATTENDANCE POINTS =====
    case 'SET_ATTENDANCEPOINTS':
      return { ...state, attendancePoints: action.payload };
    case 'ADD_ATTENDANCEPOINT':
      return { ...state, attendancePoints: [...state.attendancePoints, action.payload] };
    case 'UPDATE_ATTENDANCEPOINT':
      return {
        ...state,
        attendancePoints: state.attendancePoints.map(p =>
          p.id === action.payload.id ? { ...p, ...action.payload } : p
        )
      };
    case 'DELETE_ATTENDANCEPOINT':
      return { ...state, attendancePoints: state.attendancePoints.filter(p => p.id !== action.payload) };

    // ===== COMPLAINTS =====
    case 'SET_COMPLAINTS':
      return { ...state, complaints: action.payload };
    case 'ADD_COMPLAINT':
      return { ...state, complaints: [...state.complaints, action.payload] };
    case 'UPDATE_COMPLAINT':
      return {
        ...state,
        complaints: state.complaints.map(c =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        )
      };
    case 'DELETE_COMPLAINT':
      return { ...state, complaints: state.complaints.filter(c => c.id !== action.payload) };

    // ===== FEEDBACKS =====
    case 'SET_FEEDBACKS':
      return { ...state, feedbacks: action.payload };
    case 'ADD_FEEDBACK':
      return { ...state, feedbacks: [...state.feedbacks, action.payload] };
    case 'UPDATE_FEEDBACK':
      return {
        ...state,
        feedbacks: state.feedbacks.map(f =>
          f.id === action.payload.id ? { ...f, ...action.payload } : f
        )
      };
    case 'DELETE_FEEDBACK':
      return { ...state, feedbacks: state.feedbacks.filter(f => f.id !== action.payload) };

    // ===== EXPENSES =====
    case 'SET_EXPENSES':
      return { ...state, expenses: action.payload };
    case 'ADD_EXPENSE':
      return { ...state, expenses: [...state.expenses, action.payload] };
    case 'UPDATE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.map(e =>
          e.id === action.payload.id ? { ...e, ...action.payload } : e
        )
      };
    case 'DELETE_EXPENSE':
      return { ...state, expenses: state.expenses.filter(e => e.id !== action.payload) };

    // ===== DEPOSITS =====
    case 'SET_DEPOSITS':
      return { ...state, deposits: action.payload };
    case 'ADD_DEPOSIT':
      return { ...state, deposits: [...state.deposits, action.payload] };
    case 'UPDATE_DEPOSIT':
      return {
        ...state,
        deposits: state.deposits.map(d =>
          d.id === action.payload.id ? { ...d, ...action.payload } : d
        )
      };
    case 'DELETE_DEPOSIT':
      return { ...state, deposits: state.deposits.filter(d => d.id !== action.payload) };

    // ===== ACCOUNTS (bank/cash/credit accounts) =====
    case 'SET_ACCOUNTS':
      return { ...state, accounts: action.payload };
    case 'ADD_ACCOUNT':
      return { ...state, accounts: [...state.accounts, action.payload] };
    case 'UPDATE_ACCOUNT':
      return {
        ...state,
        accounts: state.accounts.map(a =>
          a.id === action.payload.id ? { ...a, ...action.payload } : a
        )
      };
    case 'DELETE_ACCOUNT':
      return { ...state, accounts: state.accounts.filter(a => a.id !== action.payload) };

    // ===== EXPENSE CATEGORIES (with parentId for P&L rollup) =====
    case 'SET_EXPENSECATEGORIES':
      return { ...state, expenseCategories: action.payload };
    case 'ADD_EXPENSECATEGORY':
      return { ...state, expenseCategories: [...state.expenseCategories, action.payload] };
    case 'UPDATE_EXPENSECATEGORY':
      return {
        ...state,
        expenseCategories: state.expenseCategories.map(c =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        )
      };
    case 'DELETE_EXPENSECATEGORY':
      return { ...state, expenseCategories: state.expenseCategories.filter(c => c.id !== action.payload) };

    // ===== TRANSFERS (between accounts — excluded from income/expense totals) =====
    case 'SET_TRANSFERS':
      return { ...state, transfers: action.payload };
    case 'ADD_TRANSFER':
      return { ...state, transfers: [...state.transfers, action.payload] };
    case 'UPDATE_TRANSFER':
      return {
        ...state,
        transfers: state.transfers.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload } : t
        )
      };
    case 'DELETE_TRANSFER':
      return { ...state, transfers: state.transfers.filter(t => t.id !== action.payload) };

    // ===== DAY NOTES (date-level notes shown on Dispatch) =====
    case 'SET_DAYNOTES':
      return { ...state, dayNotes: action.payload };
    case 'ADD_DAY_NOTE':
      return { ...state, dayNotes: [...state.dayNotes, action.payload] };
    case 'UPDATE_DAY_NOTE':
      return {
        ...state,
        dayNotes: state.dayNotes.map(n =>
          n.id === action.payload.id ? { ...n, ...action.payload } : n
        )
      };
    case 'DELETE_DAY_NOTE':
      return { ...state, dayNotes: state.dayNotes.filter(n => n.id !== action.payload) };

    default:
      return state;
  }
}

// Context
const AppContext = createContext(null);

// Provider
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { user } = useAuth();

  // ===== FIRESTORE SYNC =====
  
  // Load all data from Firestore
  const loadAllData = useCallback(async () => {
    if (!user) return;
    
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const data = {};
      
      for (const [key, collectionName] of Object.entries(COLLECTIONS)) {
        const snapshot = await getDocs(collection(db, collectionName));
        data[key] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      dispatch({ type: 'LOAD_DATA', payload: data });
    } catch (error) {
      console.error('Error loading data:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, [user]);

  // Set up real-time listeners
  useEffect(() => {
    if (!user) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    const unsubscribes = [];
    const collectionKeys = Object.keys(COLLECTIONS);
    const pendingFirstSnapshot = new Set(collectionKeys);

    // Set up listener for each collection
    Object.entries(COLLECTIONS).forEach(([key, collectionName]) => {
      const unsubscribe = onSnapshot(
        collection(db, collectionName),
        (snapshot) => {
          const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          dispatch({ type: `SET_${key.toUpperCase()}`, payload: items });

          // Track first snapshot arrival; mark initialized once all collections have reported
          if (pendingFirstSnapshot.has(key)) {
            pendingFirstSnapshot.delete(key);
            if (pendingFirstSnapshot.size === 0) {
              dispatch({ type: 'SET_INITIALIZED' });
            }
          }
        },
        (error) => {
          console.error(`Error listening to ${collectionName}:`, error);
          // Still count errored collections so the app doesn't stay loading forever
          if (pendingFirstSnapshot.has(key)) {
            pendingFirstSnapshot.delete(key);
            if (pendingFirstSnapshot.size === 0) {
              dispatch({ type: 'SET_INITIALIZED' });
            }
          }
        }
      );
      unsubscribes.push(unsubscribe);
    });

    // Cleanup
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [user]);

  // ===== FIRESTORE WRITE HELPERS =====

  const saveToFirestore = async (collectionName, id, data) => {
    try {
      const docRef = doc(db, collectionName, id);
      await setDoc(docRef, data, { merge: true });
      return true;
    } catch (error) {
      console.error(`Error saving to ${collectionName}:`, error);
      return false;
    }
  };

  const deleteFromFirestore = async (collectionName, id) => {
    try {
      await deleteDoc(doc(db, collectionName, id));
      return true;
    } catch (error) {
      console.error(`Error deleting from ${collectionName}:`, error);
      return false;
    }
  };

  // ===== SALES METRICS RECOMPUTE =====
  //
  // Phase 2: maintain `salesMetrics/{YYYY-MM-DD}` aggregation docs whenever a
  // lead (estimate) is written. The user prompt asked for an onWrite Cloud
  // Function trigger; this codebase deliberately avoids the Firebase Admin
  // SDK (org policy blocks service-account keys — see SCHEDULED_FUNCTIONS_SETUP.md),
  // so the equivalent is invoking this hook from every code path that mutates
  // an estimate. The Netlify backfill function rebuilds the same docs in bulk.
  //
  // We keep a ref of the latest estimates list because dispatch() is async —
  // calling this helper synchronously after dispatch would otherwise read a
  // stale closure of state.estimates. The helper merges the affected lead
  // explicitly on top of the snapshot, so single-update accuracy is preserved
  // even before the next render cycle.
  const estimatesRef = useRef([]);
  useEffect(() => {
    estimatesRef.current = state.estimates;
  }, [state.estimates]);

  const recomputeSalesMetricsForLead = useCallback(async (updatedLead, options = {}) => {
    const { previousLead = null, removed = false } = options;
    if (!updatedLead && !previousLead) return;

    // Affected days = union of the previous and updated lead's createdAt + every
    // stageHistory.enteredAt date. Covers the case where a stage entry was
    // edited or removed in a way that changes a different day's totals.
    const affected = new Set();
    if (previousLead) affectedDayKeysForLead(previousLead).forEach(d => affected.add(d));
    if (updatedLead && !removed) affectedDayKeysForLead(updatedLead).forEach(d => affected.add(d));
    if (affected.size === 0) return;

    // Build the leads view: latest snapshot with the affected lead replaced/removed/inserted.
    const id = (updatedLead && updatedLead.id) || (previousLead && previousLead.id);
    const snapshot = estimatesRef.current || [];
    const leads = snapshot.filter(l => l.id !== id);
    if (updatedLead && !removed) leads.push(updatedLead);

    const now = new Date().toISOString();
    await Promise.all([...affected].map(async (dayKey) => {
      try {
        const metrics = computeDailyMetrics(dayKey, leads);
        // Use setDoc without merge — we recompute the entire doc, so any stale
        // dimension keys (e.g. a source with no leads left on this day) must
        // be cleared rather than carried forward.
        await setDoc(doc(db, SALES_METRICS_COLLECTION, dayKey), { ...metrics, updatedAt: now });
      } catch (err) {
        // Recompute failures shouldn't break the user-facing write that
        // triggered them. Log so we can spot persistent issues.
        console.error('Failed to recompute salesMetrics for', dayKey, err);
      }
    }));
  }, []);

  // ===== SEED DATABASE =====
  
  const seedDatabase = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const batch = writeBatch(db);
      
      // Seed employees
      sampleEmployees.forEach(emp => {
        batch.set(doc(db, COLLECTIONS.employees, emp.id), emp);
      });
      
      // Seed clients
      sampleClients.forEach(client => {
        batch.set(doc(db, COLLECTIONS.clients, client.id), client);
      });
      
      // Seed properties
      sampleProperties.forEach(prop => {
        batch.set(doc(db, COLLECTIONS.properties, prop.id), prop);
      });
      
      // Seed estimates
      sampleEstimates.forEach(est => {
        batch.set(doc(db, COLLECTIONS.estimates, est.id), est);
      });
      
      // Seed jobs
      sampleJobs.forEach(job => {
        batch.set(doc(db, COLLECTIONS.jobs, job.id), job);
      });
      
      // Seed invoices
      sampleInvoices.forEach(inv => {
        batch.set(doc(db, COLLECTIONS.invoices, inv.id), inv);
      });
      
      await batch.commit();
      console.log('Database seeded successfully!');
      
      // Reload data
      await loadAllData();
      
      return { success: true };
    } catch (error) {
      console.error('Error seeding database:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  };

  // One-time backfill: ensure every estimate doc has the sales-analytics fields
  // and a seeded stageHistory. Safe to re-run — only writes when something is missing.
  const backfillSalesAnalyticsFields = async () => {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.estimates));
      let updated = 0;
      let skipped = 0;
      // Firestore caps batch writes at 500 ops; chunk to be safe.
      const docs = snapshot.docs;
      const CHUNK = 400;
      for (let i = 0; i < docs.length; i += CHUNK) {
        const batch = writeBatch(db);
        const chunk = docs.slice(i, i + CHUNK);
        let chunkWrites = 0;
        for (const d of chunk) {
          const data = d.data() || {};
          const patch = {};
          if (data.source == null) patch.source = 'unknown';
          if (data.serviceType == null) patch.serviceType = 'unknown';
          if (data.assignedTo === undefined) patch.assignedTo = null;
          if (data.serviceArea == null) patch.serviceArea = 'unknown';
          if (data.lostReason === undefined) patch.lostReason = null;
          if (data.quoteAmount === undefined || data.quoteAmount === null) {
            patch.quoteAmount = deriveQuoteAmount(data);
          }
          if (data.finalJobValue === undefined) {
            const isWon = data.status === 'approved' || data.status === 'converted' || data.leadStatus === 'won';
            patch.finalJobValue = isWon
              ? (data.approvedTotal
                  || ((data.approvedRecurringTotal || 0) + (data.approvedDeepCleanTotal || 0))
                  || deriveQuoteAmount(data))
              : null;
          }
          if (!data.leadStatus) patch.leadStatus = inferLeadStatus(data);
          if (!Array.isArray(data.stageHistory) || data.stageHistory.length === 0) {
            patch.stageHistory = seedStageHistory({ ...data, leadStatus: patch.leadStatus || data.leadStatus });
          }
          if (Object.keys(patch).length === 0) {
            skipped++;
            continue;
          }
          batch.set(d.ref, patch, { merge: true });
          chunkWrites++;
        }
        if (chunkWrites > 0) await batch.commit();
        updated += chunkWrites;
      }
      return { success: true, total: docs.length, updated, skipped };
    } catch (error) {
      console.error('Sales-analytics backfill failed:', error);
      return { success: false, error: error.message || String(error) };
    }
  };

  // Phase 2: trigger the Netlify HTTP function that rebuilds the trailing
  // 12 months of `salesMetrics/{YYYY-MM-DD}` docs from the existing estimate
  // collection. The button on Settings calls this once during rollout; the
  // onWrite hook keeps things current after that.
  const backfillSalesMetrics = async (options = {}) => {
    try {
      const url = '/.netlify/functions/backfill-sales-metrics';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ months: options.months || 12 }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) {
        return {
          success: false,
          error: body.error || `Backfill failed (HTTP ${res.status})`,
        };
      }
      return { success: true, ...body };
    } catch (error) {
      console.error('salesMetrics backfill request failed:', error);
      return { success: false, error: error.message || String(error) };
    }
  };

  // Clear all data
  const clearDatabase = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      for (const collectionName of Object.values(COLLECTIONS)) {
        const snapshot = await getDocs(collection(db, collectionName));
        const batch = writeBatch(db);
        snapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }
      
      console.log('Database cleared!');
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: true };
    } catch (error) {
      console.error('Error clearing database:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  };

  // ===== HELPER FUNCTIONS =====
  
  // Clients
  const getClientById = (id) => state.clients.find(c => c.id === id);
  
  // Employees
  const getEmployeeById = (id) => state.employees.find(e => e.id === id);
  
  // Properties
  const getPropertyById = (id) => state.properties.find(p => p.id === id);
  const getClientProperties = (clientId) => state.properties.filter(p => p.clientId === clientId);
  
  // Estimates
  const getEstimateById = (id) => state.estimates.find(e => e.id === id);
  const getClientEstimates = (clientId) => state.estimates.filter(e => e.clientId === clientId);
  const getPropertyEstimates = (propertyId) => state.estimates.filter(e => e.propertyId === propertyId);
  const getEstimateByToken = (token) => state.estimates.find(e => e.approvalToken === token);
  const getPendingEstimates = () => state.estimates.filter(e => e.status === 'sent');
  const getEstimatesRequiringReview = () => state.estimates.filter(e => e.requiresReview && e.status === 'draft');
  
  // Jobs
  const getJobById = (id) => state.jobs.find(j => j.id === id);
  const getClientJobs = (clientId) => state.jobs.filter(j => j.clientId === clientId);
  const getPropertyJobs = (propertyId) => state.jobs.filter(j => j.propertyId === propertyId);
  const getJobsByStatus = (status) => state.jobs.filter(j => j.status === status);
  const getJobsByDate = (date) => state.jobs.filter(j => j.scheduledDate === date);
  
  // Invoices
  const getInvoiceById = (id) => state.invoices.find(i => i.id === id);
  const getClientInvoices = (clientId) => state.invoices.filter(i => i.clientId === clientId);
  const getJobInvoice = (jobId) => state.invoices.find(i => i.jobId === jobId);

  // ===== ACTION HELPERS =====
  
  // Generate unique IDs
  const generateId = (prefix) => `${prefix}${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
  
  // Generate approval token
  const generateApprovalToken = () => {
    return Math.random().toString(36).substr(2, 12) + Math.random().toString(36).substr(2, 12);
  };

  // ===== CRUD OPERATIONS WITH FIRESTORE =====

  // Helper to normalize strings for comparison
  const normalizeString = (str) => {
    if (!str) return '';
    return str.toLowerCase().trim().replace(/\s+/g, ' ');
  };

  // Helper to normalize phone numbers for comparison
  const normalizePhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/\D/g, ''); // Remove all non-digits
  };

  // Find potential duplicate clients
  const findDuplicateClients = (clientData, excludeId = null) => {
    const firstName = normalizeString(clientData.firstName);
    const lastName = normalizeString(clientData.lastName);
    const email = normalizeString(clientData.email);
    const phone = normalizePhone(clientData.phone);

    return state.clients.filter(c => {
      // Skip the client being edited
      if (excludeId && c.id === excludeId) return false;

      // Check for exact email match (strongest indicator)
      if (email && normalizeString(c.email) === email) {
        return true;
      }

      // Check for exact phone match (strong indicator)
      if (phone && phone.length >= 10 && normalizePhone(c.phone) === phone) {
        return true;
      }

      // Check for exact name match
      if (firstName && lastName) {
        const existingFirst = normalizeString(c.firstName);
        const existingLast = normalizeString(c.lastName);
        if (existingFirst === firstName && existingLast === lastName) {
          return true;
        }
      }

      return false;
    });
  };

  // Find or create client - returns existing client if duplicate found, creates new otherwise
  const findOrCreateClient = async (clientData, options = {}) => {
    const { forceCreate = false, onDuplicateFound = null } = options;
    
    if (!forceCreate) {
      const duplicates = findDuplicateClients(clientData);
      if (duplicates.length > 0) {
        if (onDuplicateFound) {
          // Callback allows the UI to decide what to do
          return { existing: duplicates[0], duplicates, isNew: false };
        }
        // Default behavior: return existing client
        return { client: duplicates[0], isNew: false };
      }
    }
    
    // No duplicate found or force create, create new client
    const client = await addClient(clientData);
    return { client, isNew: true };
  };

  // Merge two clients - keeps primaryClientId, merges data from secondaryClientId
  const mergeClients = async (primaryClientId, secondaryClientId) => {
    const primaryClient = getClientById(primaryClientId);
    const secondaryClient = getClientById(secondaryClientId);
    
    if (!primaryClient || !secondaryClient) {
      throw new Error('Both clients must exist to merge');
    }

    try {
      // Merge client data - prefer primary, fill gaps from secondary
      const mergedData = {
        firstName: primaryClient.firstName || secondaryClient.firstName,
        lastName: primaryClient.lastName || secondaryClient.lastName,
        email: primaryClient.email || secondaryClient.email,
        phone: primaryClient.phone || secondaryClient.phone,
        clientType: primaryClient.clientType || secondaryClient.clientType,
        status: primaryClient.status === 'active' || secondaryClient.status === 'active' ? 'active' : primaryClient.status,
        frequency: primaryClient.frequency || secondaryClient.frequency,
        notes: [primaryClient.notes, secondaryClient.notes].filter(Boolean).join('\n---\n'),
        howFound: primaryClient.howFound || secondaryClient.howFound,
        tags: [...new Set([...(primaryClient.tags || []), ...(secondaryClient.tags || [])])],
        mergedFrom: secondaryClientId,
        mergedAt: new Date().toISOString()
      };

      // Update primary client with merged data
      await updateClient(primaryClientId, mergedData);

      // Transfer all properties from secondary to primary
      const secondaryProperties = state.properties.filter(p => p.clientId === secondaryClientId);
      for (const property of secondaryProperties) {
        await saveToFirestore(COLLECTIONS.properties, property.id, {
          clientId: primaryClientId,
          updatedAt: new Date().toISOString()
        });
      }

      // Transfer all jobs from secondary to primary
      const secondaryJobs = state.jobs.filter(j => j.clientId === secondaryClientId);
      for (const job of secondaryJobs) {
        await saveToFirestore(COLLECTIONS.jobs, job.id, {
          clientId: primaryClientId,
          clientName: `${mergedData.firstName} ${mergedData.lastName}`.trim(),
          updatedAt: new Date().toISOString()
        });
      }

      // Transfer all invoices from secondary to primary
      const secondaryInvoices = state.invoices.filter(i => i.clientId === secondaryClientId);
      for (const invoice of secondaryInvoices) {
        await saveToFirestore(COLLECTIONS.invoices, invoice.id, {
          clientId: primaryClientId,
          clientName: `${mergedData.firstName} ${mergedData.lastName}`.trim(),
          updatedAt: new Date().toISOString()
        });
      }

      // Transfer all estimates from secondary to primary
      const secondaryEstimates = state.estimates.filter(e => e.clientId === secondaryClientId);
      for (const estimate of secondaryEstimates) {
        await saveToFirestore(COLLECTIONS.estimates, estimate.id, {
          clientId: primaryClientId,
          clientName: `${mergedData.firstName} ${mergedData.lastName}`.trim(),
          updatedAt: new Date().toISOString()
        });
      }

      // Transfer all messages from secondary to primary
      const secondaryMessages = state.messages.filter(m => m.clientId === secondaryClientId);
      for (const message of secondaryMessages) {
        await saveToFirestore(COLLECTIONS.messages, message.id, {
          clientId: primaryClientId,
          clientName: `${mergedData.firstName} ${mergedData.lastName}`.trim(),
          updatedAt: new Date().toISOString()
        });
      }

      // Delete the secondary client
      await deleteClient(secondaryClientId);

      return { success: true, primaryClientId, mergedData };
    } catch (error) {
      console.error('Error merging clients:', error);
      throw error;
    }
  };

  // Add client
  const addClient = async (clientData) => {
    const client = {
      ...clientData,
      id: clientData.id || generateId('client'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Default all notification preferences to ON
      textConsent: clientData.textConsent !== undefined ? clientData.textConsent : true,
      notificationPrefs: clientData.notificationPrefs || {
        appointment: { email: true, sms: true },
        teamStatus: { email: true, sms: true },
        invoices: { email: true, sms: true },
        estimates: { email: true, sms: true },
        portalMessages: { email: true, sms: true }
      }
    };
    
    // Save to Firestore - the real-time listener will update local state via SET_CLIENTS
    // Do NOT dispatch ADD_CLIENT here as it causes duplication until the listener syncs
    const success = await saveToFirestore(COLLECTIONS.clients, client.id, client);
    
    if (!success) {
      throw new Error('Failed to save client to database');
    }
    
    return client;
  };

  // Update client
  const updateClient = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    await saveToFirestore(COLLECTIONS.clients, id, data);
  };

  // Log communication to client's history
  const logClientCommunication = async (clientId, logEntry) => {
    if (!clientId) return;
    
    const client = getClientById(clientId);
    const existingLogs = client?.communicationLog || [];
    
    const newLog = {
      id: logEntry.id || `log_${Date.now()}`,
      timestamp: new Date().toISOString(),
      ...logEntry
    };
    
    await updateClient(clientId, {
      communicationLog: [...existingLogs, newLog]
    });
    
    return newLog;
  };

  // Master password for protected operations (stored hashed in Firestore settings)
  const setMasterPassword = async (password) => {
    // Simple hash for client-side verification (not cryptographic security - Firestore rules handle real security)
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    await saveToFirestore('settings', 'masterPassword', { hash: hashHex, updatedAt: new Date().toISOString() });
    return true;
  };

  const verifyMasterPassword = async (password) => {
    try {
      const docSnap = await getDoc(doc(db, 'settings', 'masterPassword'));
      if (!docSnap.exists()) return { valid: false, notSet: true };

      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      return { valid: hashHex === docSnap.data().hash, notSet: false };
    } catch (error) {
      console.error('Error verifying master password:', error);
      return { valid: false, notSet: false };
    }
  };

  const hasMasterPassword = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'settings', 'masterPassword'));
      return docSnap.exists();
    } catch (error) {
      console.error('Error checking master password:', error);
      return false;
    }
  };

  // Delete client (requires master password verification before calling)
  const deleteClient = async (id) => {
    await deleteFromFirestore(COLLECTIONS.clients, id);
  };

  // Add employee
  const addEmployee = async (employeeData) => {
    // Extract login creation fields before saving to Firestore
    const shouldCreateLogin = employeeData._createLogin;
    const tempPassword = employeeData._tempPassword;
    const { _createLogin, _tempPassword, ...cleanData } = employeeData;

    // Create the Firebase Auth account FIRST when requested, so the returned
    // uid can be used as the Firestore doc id. firestore.rules check
    // `request.auth.uid == employeeId` (and `employee_id == request.auth.uid`)
    // for all per-user reads/writes — onboarding checklist items, signatures,
    // uploads — so the doc id must equal the auth uid for the employee to
    // access their own data.
    let authUid = null;
    let loginError = null;
    if (shouldCreateLogin && cleanData.email && tempPassword) {
      try {
        const displayName = `${cleanData.firstName} ${cleanData.lastName || ''}`.trim();
        const response = await fetch('/.netlify/functions/create-employee-account', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: cleanData.email,
            password: tempPassword,
            displayName
          })
        });
        const result = await response.json();
        if (response.ok && result.uid) {
          authUid = result.uid;
        } else {
          console.error('Failed to create login account:', result.error);
          loginError = result.error;
        }
      } catch (err) {
        console.error('Error creating login account:', err);
        loginError = 'Network error creating login account. The employee record was saved, but you may need to retry the login setup.';
      }
    }

    const employee = {
      ...cleanData,
      id: authUid || cleanData.id || generateId('emp'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (shouldCreateLogin) {
      employee.mustChangePassword = true;
    }

    // Save to Firestore - the real-time listener will update local state via SET_EMPLOYEES
    // Do NOT dispatch ADD_EMPLOYEE here as it causes duplication until the listener syncs
    await saveToFirestore(COLLECTIONS.employees, employee.id, employee);

    if (authUid) {
      // Send welcome email with login credentials
      try {
        await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'employee_welcome',
            data: {
              employeeEmail: employee.email,
              employeeName: employee.firstName,
              tempPassword
            }
          })
        });
      } catch (emailErr) {
        console.warn('Welcome email failed to send:', emailErr);
        // Don't block on email failure — account was still created
      }
      return { ...employee, _loginCreated: true };
    }

    if (loginError) {
      return { ...employee, _loginError: loginError };
    }

    return employee;
  };

  // Update employee
  const updateEmployee = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    return saveToFirestore(COLLECTIONS.employees, id, data);
  };

  // Delete employee
  const deleteEmployee = async (id) => {
    await deleteFromFirestore(COLLECTIONS.employees, id);
  };

  // Add property
  const addProperty = async (propertyData) => {
    const property = {
      ...propertyData,
      id: propertyData.id || generateId('prop'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Save to Firestore - the real-time listener will update local state via SET_PROPERTIES
    // Do NOT dispatch ADD_PROPERTY here as it causes duplication until the listener syncs
    const success = await saveToFirestore(COLLECTIONS.properties, property.id, property);
    
    if (!success) {
      throw new Error('Failed to save property to database');
    }
    
    return property;
  };

  // Update property
  const updateProperty = async (propertyId, updates) => {
    const success = await saveToFirestore(COLLECTIONS.properties, propertyId, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
    if (!success) {
      throw new Error('Failed to update property');
    }
  };

  // ===== DISPATCH ACTIVITY LOG =====
  const logDispatchActivity = async ({ action, description, jobId, jobDate, details }) => {
    const entry = {
      id: generateId('dlog'),
      action, // 'team_change' | 'route_move' | 'job_added' | 'job_deleted' | 'job_rescheduled' | 'team_assigned' | 'status_change'
      description,
      jobId: jobId || null,
      jobDate: jobDate || null,
      details: details || {},
      userId: user?.email || 'system',
      userName: user?.displayName || user?.email?.split('@')[0] || 'System',
      createdAt: new Date().toISOString(),
      comments: []
    };
    await saveToFirestore(COLLECTIONS.dispatchLog, entry.id, entry);
    return entry;
  };

  const addDispatchComment = async (logEntryId, commentText) => {
    const entry = state.dispatchLog.find(e => e.id === logEntryId);
    if (!entry) return;
    const comment = {
      id: generateId('cmt'),
      text: commentText,
      userId: user?.email || 'system',
      userName: user?.displayName || user?.email?.split('@')[0] || 'System',
      createdAt: new Date().toISOString()
    };
    const updatedComments = [...(entry.comments || []), comment];
    await saveToFirestore(COLLECTIONS.dispatchLog, logEntryId, { comments: updatedComments });
  };

  // ===== COMMUNICATION LOGS =====
  const addCommunicationLog = async (logData) => {
    const entry = {
      id: generateId('clog'),
      clientId: logData.clientId || null,
      clientName: logData.clientName || 'OTHER',
      communicationType: logData.communicationType, // 'call' | 'text' | 'email' | 'in_person'
      note: logData.note || '',
      communicationDate: logData.communicationDate, // date the communication happened
      communicationTime: logData.communicationTime, // time the communication happened
      recordedBy: user?.email || 'system',
      recordedByName: user?.displayName || user?.email?.split('@')[0] || 'System',
      createdAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.communicationLogs, entry.id, entry);

    // Also log to client's communicationLog array if tied to a client
    if (logData.clientId) {
      await logClientCommunication(logData.clientId, {
        type: logData.communicationType,
        subject: `${logData.communicationType.charAt(0).toUpperCase() + logData.communicationType.slice(1)} - logged by ${entry.recordedByName}`,
        category: logData.communicationType,
        note: logData.note,
        status: 'logged',
        recordedBy: entry.recordedByName
      });
    }

    return entry;
  };

  const deleteCommunicationLog = async (id) => {
    await deleteFromFirestore(COLLECTIONS.communicationLogs, id);
  };

  // Create estimate
  const createEstimate = async (estimateData) => {
    const now = new Date().toISOString();
    // Sales-analytics defaults: only fill fields the caller didn't supply.
    const analyticsDefaults = defaultSalesAnalyticsFields(now);
    const merged = { ...analyticsDefaults, ...estimateData };
    // Seed stageHistory based on the effective leadStatus the caller passed in.
    if (!Array.isArray(estimateData.stageHistory)) {
      merged.stageHistory = [{ stage: merged.leadStatus || 'new', enteredAt: now }];
    }
    if (merged.quoteAmount == null) {
      merged.quoteAmount = deriveQuoteAmount(merged);
    }

    const estimate = {
      ...merged,
      id: estimateData.id || generateId('est'),
      status: estimateData.status || 'draft',
      createdAt: now,
      updatedAt: now,
      createdBy: user?.email || 'website'
    };

    // Optimistic update - add to local state immediately
    dispatch({ type: 'ADD_ESTIMATE', payload: estimate });
    
    // Save to Firestore - the real-time listener will sync any changes
    const success = await saveToFirestore(COLLECTIONS.estimates, estimate.id, estimate);
    
    if (!success) {
      // Remove from local state if save failed
      dispatch({ type: 'DELETE_ESTIMATE', payload: estimate.id });
      throw new Error('Failed to save estimate to database');
    }

    // Fire-and-forget: refresh the affected day's salesMetrics doc (Phase 2).
    recomputeSalesMetricsForLead(estimate);

    return estimate;
  };

  // Update estimate
  const updateEstimate = async (id, updates) => {
    const now = new Date().toISOString();
    const data = { ...updates, updatedAt: now };

    // If leadStatus is changing, append a stageHistory entry. The append helper
    // returns null when nothing changed, so we leave stageHistory alone in that case.
    const previousLead = state.estimates.find(e => e.id === id) || null;
    if (updates.leadStatus && data.stageHistory === undefined) {
      const nextHistory = appendStageEntry(previousLead, updates.leadStatus, now);
      if (nextHistory) data.stageHistory = nextHistory;
    }

    // Update local state immediately for responsive UI
    dispatch({ type: 'UPDATE_ESTIMATE', payload: { id, ...data } });

    // Then persist to Firestore
    await saveToFirestore(COLLECTIONS.estimates, id, data);

    // Recompute the affected days' salesMetrics. We pass both the previous and
    // updated lead so the helper picks up days the lead is leaving (e.g. an
    // earlier stage transition that no longer counts) as well as the new day.
    const updatedLead = previousLead ? { ...previousLead, ...data, id } : { id, ...data };
    recomputeSalesMetricsForLead(updatedLead, { previousLead });
  };

  // Delete estimate
  const deleteEstimate = async (id, { isAdmin = false } = {}) => {
    const estimate = getEstimateById(id);
    if (!estimate) {
      console.error('Estimate not found:', id);
      return;
    }

    // Admins can delete any estimate
    if (!isAdmin) {
      const allowedStatuses = ['draft', 'pending_review', 'sent', 'declined', 'expired'];
      if (!allowedStatuses.includes(estimate.status)) {
        throw new Error(`Cannot delete estimate with status: ${estimate.status}. Only drafts, pending review, sent, declined, or expired estimates can be deleted.`);
      }
    }

    await deleteFromFirestore(COLLECTIONS.estimates, id);
    dispatch({ type: 'DELETE_ESTIMATE', payload: id });

    // Recompute the days this lead used to contribute to so the deleted lead
    // disappears from the totals.
    recomputeSalesMetricsForLead(null, { previousLead: estimate, removed: true });
  };

  // Send estimate
  const sendEstimate = async (estimateId, options = {}) => {
    const now = new Date().toISOString();
    const validityDays = options.validityDays || 14;
    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + validityDays);

    // Use provided token or generate new one
    const approvalToken = options.token || generateApprovalToken();

    const updates = {
      status: 'sent',
      leadStatus: 'estimate_sent',
      sentAt: now,
      sentVia: options.sentVia || 'email',
      approvalToken: approvalToken,
      validUntil: validUntil.toISOString(),
      updatedAt: now,
    };

    const current = state.estimates.find(e => e.id === estimateId);
    const nextHistory = appendStageEntry(current, 'estimate_sent', now);
    if (nextHistory) updates.stageHistory = nextHistory;
    if (current && current.quoteAmount == null) {
      const derived = deriveQuoteAmount(current);
      if (derived != null) updates.quoteAmount = derived;
    }

    await saveToFirestore(COLLECTIONS.estimates, estimateId, updates);

    // Also update local state for immediate UI feedback
    dispatch({ type: 'SEND_ESTIMATE', payload: { id: estimateId, ...updates } });

    // Recompute the affected days' salesMetrics. This transition is what makes
    // `quotesSent` and `quotesSentValue` increment for the day the email went out.
    if (current) {
      recomputeSalesMetricsForLead({ ...current, ...updates, id: estimateId }, { previousLead: current });
    }
  };

  // Renew an expired estimate (extends validity without changing approval token)
  const renewEstimate = async (estimateId, options = {}) => {
    const estimate = state.estimates.find(e => e.id === estimateId);
    if (!estimate) throw new Error('Estimate not found');

    const validityDays = options.validityDays || 14;
    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + validityDays);

    const updates = {
      status: 'sent',
      validUntil: validUntil.toISOString(),
      renewedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await saveToFirestore(COLLECTIONS.estimates, estimateId, updates);
    dispatch({ type: 'UPDATE_ESTIMATE', payload: { id: estimateId, ...updates } });
  };

  // Approve estimate (called from client portal)
  const approveEstimate = async (estimateId, approvalData) => {
    // Get the estimate from local state, or fetch from Firestore (for unauthenticated portal)
    let estimate = state.estimates.find(e => e.id === estimateId);
    if (!estimate) {
      try {
        const estimateDoc = await getDoc(doc(db, COLLECTIONS.estimates, estimateId));
        if (estimateDoc.exists()) {
          estimate = { id: estimateDoc.id, ...estimateDoc.data() };
        }
      } catch (err) {
        console.error('Error fetching estimate from Firestore:', err);
      }
    }
    if (!estimate) {
      console.error('Estimate not found:', estimateId);
      return;
    }

    let client = getClientById(estimate.clientId);
    if (!client && estimate.clientId) {
      try {
        const clientDoc = await getDoc(doc(db, COLLECTIONS.clients, estimate.clientId));
        if (clientDoc.exists()) {
          client = { id: clientDoc.id, ...clientDoc.data() };
        }
      } catch (err) {
        console.error('Error fetching client from Firestore:', err);
      }
    }

    let property = getPropertyById(estimate.propertyId);
    if (!property && estimate.propertyId) {
      try {
        const propertyDoc = await getDoc(doc(db, COLLECTIONS.properties, estimate.propertyId));
        if (propertyDoc.exists()) {
          property = { id: propertyDoc.id, ...propertyDoc.data() };
        }
      } catch (err) {
        console.error('Error fetching property from Firestore:', err);
      }
    }
    
    // Detect if this is a custom estimate
    const isCustomEstimate = approvalData.isCustomEstimate || 
      estimate.source === 'custom_estimate_builder' ||
      estimate.serviceType === 'commercial' ||
      estimate.serviceType === 'hoarding';

    const now = new Date().toISOString();
    const updates = {
      status: 'approved',
      leadStatus: 'won',
      approvedAt: now,
      clientNotes: approvalData.clientNotes || '',
      updatedAt: now,
    };

    // Add type-specific approval data
    if (isCustomEstimate) {
      updates.approvedLineItems = approvalData.approvedLineItems;
      updates.approvedTotal = approvalData.approvedTotal || estimate.total;
    } else {
      updates.approvedDeepCleanItems = approvalData.approvedDeepCleanItems;
      updates.approvedRecurringTotal = approvalData.approvedRecurringTotal;
      updates.approvedDeepCleanTotal = approvalData.approvedDeepCleanTotal;
      updates.approvedFrequency = approvalData.approvedFrequency;
    }

    // Stamp finalJobValue from the approved totals so analytics has a single number per won deal.
    const finalJobValue = isCustomEstimate
      ? (approvalData.approvedTotal || estimate.total || null)
      : ((approvalData.approvedDeepCleanTotal || 0) + (approvalData.approvedRecurringTotal || 0)) || null;
    if (finalJobValue != null && estimate.finalJobValue == null) {
      updates.finalJobValue = finalJobValue;
    }

    const nextHistory = appendStageEntry(estimate, 'won', now);
    if (nextHistory) updates.stageHistory = nextHistory;

    await saveToFirestore(COLLECTIONS.estimates, estimateId, updates);
    dispatch({ type: 'APPROVE_ESTIMATE', payload: { id: estimateId, ...updates } });

    // Recompute the affected days' salesMetrics. This transition is what makes
    // `won` and `wonValue` increment for the day the client approved.
    recomputeSalesMetricsForLead({ ...estimate, ...updates, id: estimateId }, { previousLead: estimate });

    // Get selected addon names for the email (residential only)
    const selectedAddonNames = approvalData.approvedDeepCleanItems
      ?.filter(item => item.type === 'addon' && item.approvedByClient)
      ?.map(item => item.label) || [];

    // Get frequency label
    const frequencyLabels = {
      weekly: 'Weekly',
      biweekly: 'Bi-Weekly',
      monthly: 'Monthly',
      one_time: 'One-Time'
    };
    const frequencyLabel = frequencyLabels[approvalData.approvedFrequency] || approvalData.approvedFrequency || 'Custom';

    // Format currency for email
    const formatCurr = (val) => {
      if (!val) return null;
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
    };
    
    // Get property address with fallback for custom estimates
    const propertyAddress = property 
      ? `${property.addressLine1 || property.street}, ${property.city}, ${property.state} ${property.zip || property.zipCode}`
      : estimate.propertyAddress || estimate.propertyInfo?.street 
        ? `${estimate.propertyInfo.street}, ${estimate.propertyInfo.city}, ${estimate.propertyInfo.state} ${estimate.propertyInfo.zip}`
        : 'Address to be confirmed';

    // Run post-approval tasks in the background so the client sees the confirmation page immediately
    const backgroundTasks = async () => {
      // Send notification email to staff
      try {
        const { sendEstimateApprovedNotification } = await import('../utils/emailApi');
        await sendEstimateApprovedNotification({
          clientName: client ? `${client.firstName} ${client.lastName}` : estimate.clientName || 'Unknown Client',
          clientEmail: client?.email || estimate.clientEmail || '',
          clientPhone: client?.phone || estimate.clientPhone || '',
          propertyAddress,
          selectedFrequency: isCustomEstimate ? `Custom (${estimate.title || 'Quote'})` : frequencyLabel,
          recurringPrice: isCustomEstimate ? formatCurr(estimate.total) : formatCurr(approvalData.approvedRecurringTotal),
          deepCleanPrice: isCustomEstimate ? null : formatCurr(approvalData.approvedDeepCleanTotal),
          selectedAddons: selectedAddonNames,
          clientNotes: approvalData.clientNotes || '',
          isCustomEstimate
        });
      } catch (emailError) {
        console.error('Failed to send approval notification email:', emailError);
      }

      // Create an unscheduled job for the first visit
      try {
        const jobData = {
          clientId: estimate.clientId,
          propertyId: estimate.propertyId,
          estimateId: estimateId,
          clientName: client ? `${client.firstName} ${client.lastName}` : estimate.clientName || 'Unknown Client',
          address: propertyAddress,
          status: 'unscheduled',
          date: null,
          scheduledTime: null,
          price: isCustomEstimate
            ? (approvalData.approvedTotal || estimate.total)
            : (approvalData.approvedDeepCleanTotal || approvalData.approvedRecurringTotal),
          isDeepClean: !isCustomEstimate && approvalData.approvedDeepCleanTotal > 0,
          isCustomJob: isCustomEstimate,
          frequency: isCustomEstimate ? 'one_time' : approvalData.approvedFrequency,
          recurringPrice: isCustomEstimate ? null : approvalData.approvedRecurringTotal,
          notes: approvalData.clientNotes || '',
          team: [],
          duration: 0
        };

        await addJob(jobData);
      } catch (jobError) {
        console.error('Failed to create unscheduled job:', jobError);
      }

      // Update client status from lead to active
      if (client && client.status === 'lead') {
        try {
          await saveToFirestore(COLLECTIONS.clients, client.id, {
            status: 'active',
            frequency: isCustomEstimate ? 'custom' : approvalData.approvedFrequency,
            updatedAt: new Date().toISOString()
          });
        } catch (clientError) {
          console.error('Failed to update client status:', clientError);
        }
      }

      // Send welcome email with portal instructions
      if (client?.email) {
        try {
          const { sendWelcomeEmail } = await import('../utils/emailApi');
          await sendWelcomeEmail({
            clientId: client.id,
            clientEmail: client.email,
            clientName: client ? `${client.firstName} ${client.lastName}` : estimate.clientName,
            portalLink: 'https://keelgroup.app/portal',
            address: propertyAddress,
            serviceType: isCustomEstimate ? (estimate.title || 'Custom Service') : frequencyLabel + ' Cleaning',
            isExistingClient: false
          });

          // Mark welcome email as sent on the client
          await saveToFirestore(COLLECTIONS.clients, client.id, {
            welcomeEmailSent: true,
            welcomeEmailSentAt: new Date().toISOString()
          });

          console.log('Welcome email sent to:', client.email);
        } catch (welcomeError) {
          console.error('Failed to send welcome email:', welcomeError);
        }
      }
    };

    // Fire background tasks without awaiting - client sees confirmation immediately
    backgroundTasks().catch(err => console.error('Background approval tasks error:', err));
  };

  // Add job
  const addJob = async (jobData) => {
    // Auto-compute routeId/routeName from team when missing
    let routeId = jobData.routeId;
    let routeName = jobData.routeName;
    if (!routeId && jobData.team && jobData.team.length > 0) {
      const sortedIds = [...jobData.team].sort();
      routeId = `team_${sortedIds.join('_')}`;
      routeName = (jobData.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ') || null;
    }
    const job = {
      ...jobData,
      id: jobData.id || generateId('job'),
      routeId: routeId || null,
      routeName: routeName || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.jobs, job.id, job);
    // Note: Local state will be updated by the Firestore real-time listener
    return job;
  };

  // Update job
  const updateJob = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    await saveToFirestore(COLLECTIONS.jobs, id, data);
    
    // Note: Local state will be updated by the Firestore real-time listener
    // We don't dispatch here to avoid race conditions
    
    // Check if job was just completed and client has autoInvoice enabled
    if (updates.status === 'completed') {
      const job = getJobById(id);
      if (job) {
        const client = getClientById(job.clientId);
        if (client?.autoInvoice) {
          // Check if a deposit invoice already exists for this job
          const existingInvoice = state.invoices.find(
            inv => inv.jobId === id && inv.status !== 'cancelled'
          );

          if (existingInvoice && existingInvoice.requestDeposit && existingInvoice.depositPaid) {
            // Deposit invoice exists and deposit was paid — send the remaining balance
            // Reset the payment token so a fresh pay link is generated on send
            const remainingBalance = Math.max(0, (existingInvoice.total || 0) - (existingInvoice.amountPaid || 0));
            if (remainingBalance > 0) {
              await saveToFirestore(COLLECTIONS.invoices, existingInvoice.id, {
                balance: remainingBalance,
                depositInvoiceSent: false,
                updatedAt: new Date().toISOString()
              });
            }
            // Don't create a new invoice — the existing one covers the remaining balance
          } else if (!existingInvoice) {
            // No existing invoice — auto-create draft invoice
            const fullJob = { ...job, ...updates };
            await createInvoiceForJob(fullJob, client);
          }
          // If an existing invoice without deposit, or deposit not yet paid, skip — don't duplicate
        }
      }
    }
  };

  // Delete job
  const deleteJob = async (id) => {
    await deleteFromFirestore(COLLECTIONS.jobs, id);
    // Note: Local state will be updated by the Firestore real-time listener
  };

  // Toggle a custom checklist item on a job (used by staff in the field)
  const updateJobChecklistItem = async (jobId, itemId, completed, employeeId) => {
    const job = getJobById(jobId);
    if (!job) return;
    const existing = job.checklistCompletion || {};
    const next = {
      ...existing,
      [itemId]: completed
        ? { completed: true, completedAt: new Date().toISOString(), completedBy: employeeId || null }
        : { completed: false, completedAt: null, completedBy: null }
    };
    await updateJob(jobId, { checklistCompletion: next });
  };

  // Delete all scheduled jobs in a recurring series
  const deleteSeriesJobs = async (recurringScheduleId, options = {}) => {
    const { scope = 'all_scheduled', fromDate = null } = options;
    if (!recurringScheduleId) throw new Error('No recurring schedule ID provided');

    const seriesJobs = state.jobs.filter(j => j.recurringScheduleId === recurringScheduleId);
    let jobsToDelete = [];

    if (scope === 'all_scheduled') {
      // Delete all scheduled (future) jobs in this series
      jobsToDelete = seriesJobs.filter(j => j.status === 'scheduled');
    } else if (scope === 'future' && fromDate) {
      // Delete future scheduled jobs from a specific date
      jobsToDelete = seriesJobs.filter(j => j.status === 'scheduled' && j.date >= fromDate);
    } else if (scope === 'all') {
      // Delete ALL jobs in series (scheduled + cancelled, keep completed)
      jobsToDelete = seriesJobs.filter(j => j.status !== 'completed');
    }

    for (const job of jobsToDelete) {
      await deleteFromFirestore(COLLECTIONS.jobs, job.id);
    }

    // Also delete the recurring schedule itself if deleting all
    if (scope === 'all_scheduled' || scope === 'all') {
      try {
        await deleteFromFirestore('recurringSchedules', recurringScheduleId);
      } catch (e) {
        console.warn('Could not delete recurring schedule:', e);
      }
    }

    return { deleted: jobsToDelete.length, jobs: jobsToDelete };
  };

  // Delete a property
  const deleteProperty = async (propertyId) => {
    await deleteFromFirestore(COLLECTIONS.properties, propertyId);
  };

  // Merge two properties - keeps primary, transfers jobs/estimates from secondary, deletes secondary
  const mergeProperties = async (primaryPropertyId, secondaryPropertyId) => {
    const primaryProp = state.properties.find(p => p.id === primaryPropertyId);
    const secondaryProp = state.properties.find(p => p.id === secondaryPropertyId);

    if (!primaryProp || !secondaryProp) {
      throw new Error('Both properties must exist to merge');
    }

    try {
      // Merge property data - prefer primary, fill gaps from secondary
      const mergedData = {
        addressLine1: primaryProp.addressLine1 || secondaryProp.addressLine1,
        city: primaryProp.city || secondaryProp.city,
        state: primaryProp.state || secondaryProp.state,
        zip: primaryProp.zip || secondaryProp.zip,
        sqft: primaryProp.sqft || secondaryProp.sqft,
        bedrooms: primaryProp.bedrooms || secondaryProp.bedrooms,
        bathrooms: primaryProp.bathrooms || secondaryProp.bathrooms,
        pets: primaryProp.pets || secondaryProp.pets,
        accessInfo: [primaryProp.accessInfo, secondaryProp.accessInfo].filter(Boolean).join('\n'),
        photoUrl: primaryProp.photoUrl || secondaryProp.photoUrl,
        coordinates: primaryProp.coordinates || secondaryProp.coordinates,
        isPrimary: primaryProp.isPrimary || secondaryProp.isPrimary,
        mergedFrom: secondaryPropertyId,
        mergedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Update primary property
      await saveToFirestore(COLLECTIONS.properties, primaryPropertyId, mergedData);

      // Transfer jobs from secondary property to primary
      const secondaryJobs = state.jobs.filter(j => j.propertyId === secondaryPropertyId);
      const primaryAddress = mergedData.addressLine1;
      const primaryFullAddress = `${mergedData.addressLine1}, ${mergedData.city}, ${mergedData.state} ${mergedData.zip}`;

      for (const job of secondaryJobs) {
        await saveToFirestore(COLLECTIONS.jobs, job.id, {
          propertyId: primaryPropertyId,
          address: primaryAddress,
          fullAddress: primaryFullAddress,
          updatedAt: new Date().toISOString()
        });
      }

      // Transfer estimates from secondary property to primary
      const secondaryEstimates = state.estimates.filter(e => e.propertyId === secondaryPropertyId);
      for (const est of secondaryEstimates) {
        await saveToFirestore(COLLECTIONS.estimates, est.id, {
          propertyId: primaryPropertyId,
          propertyAddress: primaryFullAddress,
          updatedAt: new Date().toISOString()
        });
      }

      // Delete the secondary property
      await deleteFromFirestore(COLLECTIONS.properties, secondaryPropertyId);

      return { success: true, primaryPropertyId, mergedData };
    } catch (error) {
      console.error('Error merging properties:', error);
      throw error;
    }
  };

  // ===== INVOICE FUNCTIONS =====
  
  // Create invoice for a job
  const createInvoiceForJob = async (job, client) => {
    const invoice = createInvoiceFromJob(job, client);
    await saveToFirestore(COLLECTIONS.invoices, invoice.id, invoice);
    // Note: Local state will be updated by the Firestore real-time listener
    return invoice;
  };
  
  // Create manual invoice
  const createInvoice = async (invoiceData) => {
    const invoice = {
      ...invoiceData,
      id: invoiceData.id || generateId('inv'),
      invoiceNumber: invoiceData.invoiceNumber || generateInvoiceNumber(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.invoices, invoice.id, invoice);
    // Note: Local state will be updated by the Firestore real-time listener
    return invoice;
  };

  // Add invoice (generic - for manual invoice creation)
  const addInvoice = async (invoiceData) => {
    const invoice = {
      ...invoiceData,
      id: invoiceData.id || `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: invoiceData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    console.log('Creating invoice:', invoice.id, invoice.invoiceNumber);
    
    const success = await saveToFirestore(COLLECTIONS.invoices, invoice.id, invoice);
    
    if (!success) {
      console.error('Failed to save invoice to Firestore');
      throw new Error('Failed to save invoice to database');
    }
    
    console.log('Invoice saved successfully:', invoice.id);
    
    // Immediately update local state (listener will dedupe if it fires)
    dispatch({ type: 'ADD_INVOICE', payload: invoice });
    
    return invoice;
  };
  
  // Update invoice
  const updateInvoice = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    await saveToFirestore(COLLECTIONS.invoices, id, data);
    // Note: Local state will be updated by the Firestore real-time listener
  };
  
  // Delete invoice (only drafts)
  const deleteInvoice = async (id) => {
    await deleteFromFirestore(COLLECTIONS.invoices, id);
    // Local state updated by onSnapshot listener
  };

  // Convert estimate to job - creates deep clean + recurring schedule
  const convertEstimateToJob = async (estimateId, jobData) => {
    const estimate = getEstimateById(estimateId);
    if (!estimate) {
      console.error('Estimate not found');
      return null;
    }

    // Check if there's already an unscheduled job for this estimate
    const existingJob = state.jobs.find(j => 
      j.estimateId === estimateId && 
      (j.status === 'unscheduled' || !j.date)
    );

    // Check if this is a custom estimate
    const isCustomEstimate = jobData.isCustomEstimate || checkCustomEstimate(estimate);

    // Look up client and property for their names
    const client = getClientById(estimate.clientId);
    const property = getPropertyById(estimate.propertyId);
    
    const clientName = client 
      ? `${client.firstName} ${client.lastName}`.trim()
      : estimate.clientName || 'Unknown Client';
    
    // Handle property address for both residential and custom estimates
    const propertyAddress = property?.addressLine1 || 
      estimate.propertyAddress || 
      estimate.propertyInfo?.street || 
      '';
    const propertyCity = property?.city || 
      estimate.calculatorInputs?.city || 
      estimate.propertyInfo?.city || 
      'Charlottesville';
    const propertyState = property?.state || 
      estimate.calculatorInputs?.state || 
      estimate.propertyInfo?.state || 
      'VA';
    const propertyZip = property?.zipCode || 
      estimate.calculatorInputs?.zipCode || 
      estimate.propertyInfo?.zip || 
      '';
    const fullAddress = `${propertyAddress}, ${propertyCity}, ${propertyState} ${propertyZip}`.trim();

    // Determine frequency - for custom estimates, use the customRecurringFrequency if provided
    const frequency = isCustomEstimate 
      ? (jobData.customRecurringFrequency || 'one_time')
      : estimate.calculatorInputs?.frequency;
    
    const isRecurring = isCustomEstimate
      ? (frequency && frequency !== 'one_time' && jobData.isRecurring)
      : (frequency && frequency !== 'one_time' && jobData.isRecurring);
    
    const hasDeepClean = !isCustomEstimate && (estimate.deepCleanTotal > 0 || estimate.approvedDeepCleanTotal > 0);
    
    // Determine pricing - round to cents
    // For custom estimates, use the total; for residential, use deep clean or recurring
    const deepCleanPrice = Math.round((estimate.approvedDeepCleanTotal || estimate.deepCleanTotal || 0) * 100) / 100;
    const recurringPrice = Math.round((estimate.approvedRecurringTotal || estimate.recurringTotal || 0) * 100) / 100;
    const customPrice = Math.round((jobData.customPrice || estimate.total || 0) * 100) / 100;

    // Base job properties shared between deep clean and recurring
    const baseJobProps = {
      estimateId: estimateId,
      clientId: estimate.clientId,
      propertyId: estimate.propertyId,
      clientName: clientName,
      address: propertyAddress,
      city: propertyCity,
      state: propertyState,
      zip: propertyZip,
      fullAddress: fullAddress,
      scheduledTime: jobData.time,
      scheduledEndTime: jobData.endTime,
      duration: estimate.calculatorInputs?.estimatedHours || 3,
      team: jobData.team || [],
      teamNames: jobData.teamNames || [],
      routeId: (() => { const ids = [...(jobData.team || [])].sort(); return ids.length > 0 ? `team_${ids.join('_')}` : null; })(),
      routeName: (jobData.teamNames || []).length > 0 ? (jobData.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ') : null,
      frequency: frequency,
      lat: null,
      lng: null,
      driveTimeFromPrev: null,
      driveDistanceFromPrev: null,
      clientPhone: client?.phone || estimate.clientPhone || '',
      clientEmail: client?.email || estimate.clientEmail || '',
      // Property details for team - handle both residential and custom
      sqft: estimate.calculatorInputs?.sqft || estimate.propertyInfo?.sqft || property?.sqft || null,
      bedrooms: estimate.calculatorInputs?.bedrooms || estimate.propertyInfo?.bedrooms || property?.bedrooms || null,
      bathrooms: estimate.calculatorInputs?.bathrooms || estimate.propertyInfo?.bathrooms || property?.bathrooms || null,
      homeCondition: estimate.calculatorInputs?.homeCondition || null,
      pets: estimate.calculatorInputs?.pets || property?.pets || null,
      petDetails: estimate.calculatorInputs?.petDetails || property?.petDetails || '',
      accessInstructions: property?.accessInstructions || estimate.accessInstructions || estimate.propertyInfo?.propertyNotes || '',
      clientNotes: estimate.clientNotes || estimate.notes || client?.notes || '',
      specialInstructions: estimate.specialInstructions || property?.specialInstructions || '',
      // Add-ons and line items for scope of work
      selectedAddons: estimate.selectedAddons || [],
      lineItems: estimate.lineItems || [],
      // Custom estimate flag
      isCustomEstimate: isCustomEstimate,
      estimateTitle: estimate.title || null,
    };

    // Determine price based on estimate type
    const getJobPrice = (isFirstClean) => {
      if (isCustomEstimate) {
        return customPrice;
      }
      if (isFirstClean && hasDeepClean) {
        return deepCleanPrice;
      }
      return recurringPrice;
    };

    // Determine service type
    const getServiceType = (isFirstClean) => {
      if (isCustomEstimate) {
        return estimate.serviceType || 'custom';
      }
      if (isFirstClean && hasDeepClean) {
        return 'deep_clean';
      }
      return frequency;
    };

    // 1. Create or UPDATE the FIRST job (deep clean if applicable, otherwise first recurring)
    let firstJob;
    
    if (existingJob) {
      // Update existing unscheduled job instead of creating new
      firstJob = {
        ...existingJob,
        ...baseJobProps,
        status: 'scheduled',
        date: jobData.date,
        serviceType: getServiceType(true),
        price: getJobPrice(true),
        isFirstClean: true,
        isRecurring: false,
        notes: jobData.notes || existingJob.notes || '',
        updatedAt: new Date().toISOString()
      };
      await saveToFirestore(COLLECTIONS.jobs, existingJob.id, firstJob);
    } else {
      // Create new job
      firstJob = {
        ...baseJobProps,
        id: generateId('job'),
        status: 'scheduled',
        date: jobData.date,
        serviceType: getServiceType(true),
        price: getJobPrice(true),
        isFirstClean: true,
        isRecurring: false,
        notes: jobData.notes || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      await saveToFirestore(COLLECTIONS.jobs, firstJob.id, firstJob);
    }

    let recurringScheduleId = null;

    // 2. If recurring, create a recurring schedule that generates future jobs
    if (isRecurring) {
      // Calculate the first recurring date (after deep clean)
      const firstJobDate = new Date(jobData.date);
      let firstRecurringDate = new Date(firstJobDate);
      
      // Add interval based on frequency
      if (frequency === 'weekly') {
        firstRecurringDate.setDate(firstRecurringDate.getDate() + 7);
      } else if (frequency === 'biweekly') {
        firstRecurringDate.setDate(firstRecurringDate.getDate() + 14);
      } else if (frequency === 'monthly') {
        firstRecurringDate.setMonth(firstRecurringDate.getMonth() + 1);
      } else if (frequency === 'quarterly') {
        firstRecurringDate.setMonth(firstRecurringDate.getMonth() + 3);
      }

      // For custom estimates, use the custom price for recurring jobs too
      const recurringJobPrice = isCustomEstimate ? customPrice : recurringPrice;

      // Create recurring schedule record
      const recurringSchedule = {
        id: generateId('rec'),
        estimateId: estimateId,
        clientId: estimate.clientId,
        propertyId: estimate.propertyId,
        clientName: clientName,
        clientEmail: client?.email || estimate.clientEmail || '',
        clientPhone: client?.phone || estimate.clientPhone || '',
        address: propertyAddress,
        city: propertyCity,
        state: propertyState,
        zip: propertyZip,
        fullAddress: fullAddress,
        frequency: frequency,
        preferredDay: firstJobDate.getDay(), // 0=Sunday, 6=Saturday
        preferredTime: jobData.time,
        scheduledTime: jobData.time,
        scheduledEndTime: jobData.endTime,
        duration: estimate.calculatorInputs?.estimatedHours || 3,
        price: recurringPrice,
        defaultTeam: jobData.team || [],
        defaultTeamNames: jobData.teamNames || [],
        team: jobData.team || [],
        teamNames: jobData.teamNames || [],
        startDate: firstRecurringDate.toISOString().split('T')[0],
        endDate: null, // null = indefinite
        status: 'active',
        isActive: true,
        lastGeneratedDate: null,
        // Property details for job generation
        sqft: estimate.calculatorInputs?.sqft || property?.sqft || null,
        bedrooms: estimate.calculatorInputs?.bedrooms || property?.bedrooms || null,
        bathrooms: estimate.calculatorInputs?.bathrooms || property?.bathrooms || null,
        pets: estimate.calculatorInputs?.pets || property?.pets || null,
        petDetails: estimate.calculatorInputs?.petDetails || property?.petDetails || '',
        accessInstructions: property?.accessInstructions || estimate.accessInstructions || '',
        clientNotes: estimate.clientNotes || client?.notes || '',
        specialInstructions: estimate.specialInstructions || property?.specialInstructions || '',
        selectedAddons: estimate.selectedAddons || [],
        notes: jobData.notes || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await saveToFirestore('recurringSchedules', recurringSchedule.id, recurringSchedule);
      recurringScheduleId = recurringSchedule.id;

      // Generate the next 8 weeks of recurring jobs upfront
      const jobsToCreate = [];
      let nextDate = new Date(firstRecurringDate);
      const weeksToGenerate = 8;

      for (let i = 0; i < weeksToGenerate; i++) {
        // Check if we should create a job for this date based on frequency
        let shouldCreate = false;
        if (frequency === 'weekly') {
          shouldCreate = true;
        } else if (frequency === 'biweekly') {
          shouldCreate = i % 2 === 0;
        } else if (frequency === 'monthly') {
          shouldCreate = i === 0; // Only first one for monthly, we'll handle monthly differently
        }

        if (shouldCreate || frequency === 'monthly') {
          const recurringJob = {
            ...baseJobProps,
            id: generateId('job'),
            status: 'scheduled',
            date: nextDate.toISOString().split('T')[0],
            serviceType: 'recurring',
            price: recurringPrice,
            isFirstClean: false,
            isRecurring: true,
            recurringScheduleId: recurringSchedule.id,
            notes: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          jobsToCreate.push(recurringJob);
        }

        // Move to next interval
        if (frequency === 'weekly') {
          nextDate.setDate(nextDate.getDate() + 7);
        } else if (frequency === 'biweekly') {
          nextDate.setDate(nextDate.getDate() + 7); // We check every week but only create biweekly
        } else if (frequency === 'monthly') {
          nextDate.setMonth(nextDate.getMonth() + 1);
        }

        // Stop at 8 jobs max for initial generation
        if (jobsToCreate.length >= 8) break;
      }

      // Save all recurring jobs
      for (const job of jobsToCreate) {
        await saveToFirestore(COLLECTIONS.jobs, job.id, job);
      }

      // Update recurring schedule with last generated date
      await saveToFirestore('recurringSchedules', recurringSchedule.id, {
        lastGeneratedDate: jobsToCreate.length > 0 
          ? jobsToCreate[jobsToCreate.length - 1].date 
          : null
      });
    }

    // 3. Auto-create full prepayment invoice for new client first cleans
    // New clients must pay in full before their appointment date to secure the booking.
    let depositInvoice = null;
    if (firstJob.isFirstClean && client) {
      // Check if client has any paid invoices (truly new client) and is not exempt
      const hasPaidInvoices = state.invoices.some(
        inv => inv.clientId === client.id && inv.status === 'paid'
      );
      if (!hasPaidInvoices && !client.depositExempt) {
        const invoiceDate = new Date().toISOString().split('T')[0];
        const total = firstJob.price || 0;

        // Payment is due before the appointment date (the day before, but never in the past)
        let prepaymentDueDate = invoiceDate;
        if (firstJob.date) {
          const apptDate = new Date(`${firstJob.date}T00:00:00`);
          apptDate.setDate(apptDate.getDate() - 1);
          const dayBefore = apptDate.toISOString().split('T')[0];
          prepaymentDueDate = dayBefore > invoiceDate ? dayBefore : invoiceDate;
        }

        depositInvoice = {
          id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          invoiceNumber: generateInvoiceNumber(),
          clientId: client.id,
          clientName: clientName,
          clientEmail: client.accountingEmail || client.email || null,
          clientPhone: client.phone || null,
          jobId: firstJob.id,
          jobDate: firstJob.date,
          propertyAddress: firstJob.fullAddress || firstJob.address || '',
          status: 'draft',
          invoiceDate,
          dueDate: prepaymentDueDate,
          paymentTerms: 'due_on_receipt',
          lineItems: [{
            id: `li_${Date.now()}_prepay`,
            type: 'service',
            description: `${firstJob.serviceType === 'deep_clean' ? 'Initial Deep Clean' : 'First Cleaning Service'}`,
            quantity: 1,
            unitPrice: total,
            total: total
          }],
          subtotal: total,
          tax: 0,
          total: total,
          requestDeposit: false,
          depositType: null,
          depositValue: null,
          depositAmount: 0,
          depositDueDate: null,
          depositPaid: false,
          isFirstCleanInvoice: true,
          isPrepaymentInvoice: true,
          fullServiceTotal: total,
          tipAmount: 0,
          amountPaid: 0,
          balance: total,
          notes: `Full payment due before your ${firstJob.date} appointment to secure your booking.`,
          stripeInvoiceId: null,
          stripePaymentUrl: null,
          stripeCustomerId: client.stripeCustomerId || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          sentAt: null,
          paidAt: null
        };

        await saveToFirestore(COLLECTIONS.invoices, depositInvoice.id, depositInvoice);
        console.log('Auto-created full prepayment invoice for new client:', depositInvoice.invoiceNumber);
      }
    }

    // 4. Update estimate status
    await saveToFirestore(COLLECTIONS.estimates, estimateId, {
      status: 'converted',
      convertedToJobId: firstJob.id,
      recurringScheduleId: recurringScheduleId,
      depositInvoiceId: depositInvoice?.id || null,
      convertedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // 5. Update client with frequency if recurring
    if (isRecurring && client) {
      await saveToFirestore(COLLECTIONS.clients, client.id, {
        frequency: frequency,
        recurringScheduleId: recurringScheduleId,
        updatedAt: new Date().toISOString()
      });
    }

    return firstJob;
  };

  // ===== MESSAGE FUNCTIONS =====

  // Get message by ID
  const getMessageById = (id) => {
    return state.messages.find(m => m.id === id);
  };

  // Get messages for a specific client
  const getClientMessages = (clientId) => {
    return state.messages
      .filter(m => m.clientId === clientId)
      .sort((a, b) => new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt));
  };

  // Get unread count for admin (new messages or messages with unread client replies)
  const getAdminUnreadCount = () => {
    return state.messages.filter(m => 
      !m.archived &&
      (m.status === 'new' || 
      (m.lastClientMessageAt && m.lastAdminReadAt && new Date(m.lastClientMessageAt) > new Date(m.lastAdminReadAt)) ||
      (m.lastClientMessageAt && !m.lastAdminReadAt))
    ).length;
  };

  // Get unread count for a specific client
  const getClientUnreadCount = (clientId) => {
    return state.messages
      .filter(m => m.clientId === clientId)
      .filter(m => 
        m.lastAdminMessageAt && 
        (!m.lastClientReadAt || new Date(m.lastAdminMessageAt) > new Date(m.lastClientReadAt))
      ).length;
  };

  // Auto-detect message type based on content
  const detectMessageType = (subject, body) => {
    const text = `${subject} ${body}`.toLowerCase();
    
    if (text.match(/reschedule|move.*appointment|change.*date|different.*day/)) {
      return 'reschedule';
    }
    if (text.match(/add.*fridge|add.*oven|add.*window|add.*baseboard|add.*cabinet|extra.*service|add-on|addon/)) {
      return 'addon';
    }
    if (text.match(/laundry|pickup|wash.*fold/)) {
      return 'laundry';
    }
    if (text.match(/complaint|unhappy|problem|issue|disappointed|upset/)) {
      return 'complaint';
    }
    if (text.match(/thank|great|love|amazing|wonderful|awesome|excellent/)) {
      return 'feedback';
    }
    if (text.match(/urgent|asap|emergency|immediately/)) {
      return 'urgent';
    }
    
    return 'general';
  };

  // Detect priority based on content
  const detectPriority = (subject, body) => {
    const text = `${subject} ${body}`.toLowerCase();
    
    if (text.match(/urgent|asap|emergency|immediately/)) {
      return 'urgent';
    }
    if (text.match(/complaint|unhappy|problem|disappointed/)) {
      return 'high';
    }
    
    return 'normal';
  };

  // Create a new message/conversation
  const addMessage = async (messageData) => {
    const now = new Date().toISOString();
    const client = getClientById(messageData.clientId);
    
    // Auto-detect type and priority if not provided
    const autoType = messageData.type || detectMessageType(messageData.subject || '', messageData.body || '');
    const autoPriority = messageData.priority || detectPriority(messageData.subject || '', messageData.body || '');
    
    const message = {
      id: generateId('msg'),
      conversationId: generateId('conv'),
      
      // Client info (denormalized)
      clientId: messageData.clientId,
      clientName: client ? `${client.firstName} ${client.lastName}`.trim() : (messageData.clientName || messageData.senderName || 'Unknown'),
      clientEmail: client?.email || messageData.clientEmail || messageData.senderEmail || '',
      clientPhone: client?.phone || messageData.clientPhone || '',
      
      // Message content
      subject: messageData.subject || 'No subject',
      type: autoType,
      priority: autoPriority,
      status: messageData.sender === 'admin' ? 'in_progress' : 'new',
      
      // Assignment
      assignedTo: null,
      assignedToName: null,
      
      // Thread
      thread: [
        {
          id: generateId('thr'),
          body: messageData.body || '',
          sender: messageData.sender || 'client',
          senderName: messageData.senderName || (client ? `${client.firstName} ${client.lastName}`.trim() : 'Client'),
          senderEmail: messageData.senderEmail || client?.email || '',
          timestamp: now,
          readAt: null
        }
      ],
      
      // Timestamps
      createdAt: now,
      updatedAt: now,
      lastMessageAt: now,
      lastClientMessageAt: messageData.sender === 'admin' ? null : now,
      lastAdminMessageAt: messageData.sender === 'admin' ? now : null,
      lastAdminReadAt: messageData.sender === 'admin' ? now : null,
      lastClientReadAt: null,
      resolvedAt: null,
      
      // Source tracking
      source: messageData.source || 'portal',
      
      // Related records
      relatedJobId: messageData.relatedJobId || null,
      relatedEstimateId: messageData.relatedEstimateId || null,
      relatedInvoiceId: messageData.relatedInvoiceId || null
    };
    
    await saveToFirestore(COLLECTIONS.messages, message.id, message);
    dispatch({ type: 'ADD_MESSAGE', payload: message });
    return message;
  };

  // Add reply to existing conversation
  const addReply = async (messageId, replyData) => {
    const message = getMessageById(messageId);
    if (!message) {
      console.error('Message not found:', messageId);
      return null;
    }
    
    const now = new Date().toISOString();
    const isAdmin = replyData.sender === 'admin';
    
    const newReply = {
      id: generateId('thr'),
      body: replyData.body,
      sender: replyData.sender,
      senderName: replyData.senderName,
      senderEmail: replyData.senderEmail || '',
      timestamp: now,
      readAt: null
    };
    
    const updates = {
      thread: [...message.thread, newReply],
      updatedAt: now,
      lastMessageAt: now,
      // Update status based on who is replying
      status: isAdmin ? 'in_progress' : (message.status === 'resolved' ? 'in_progress' : message.status),
    };
    
    // Track last message time by sender type
    if (isAdmin) {
      updates.lastAdminMessageAt = now;
    } else {
      updates.lastClientMessageAt = now;
    }
    
    // If resolved and client replies, reopen
    if (message.status === 'resolved' && !isAdmin) {
      updates.resolvedAt = null;
    }
    
    await saveToFirestore(COLLECTIONS.messages, messageId, updates);
    return { ...message, ...updates };
  };

  // Update message (status, priority, type, assignment)
  const updateMessage = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    
    // If marking as resolved, set resolvedAt
    if (updates.status === 'resolved') {
      data.resolvedAt = new Date().toISOString();
    }
    
    await saveToFirestore(COLLECTIONS.messages, id, data);
  };

  // Mark message as read by admin
  const markMessageReadByAdmin = async (messageId) => {
    const message = getMessageById(messageId);
    if (!message) return;
    
    const now = new Date().toISOString();
    const updates = {
      lastAdminReadAt: now,
      updatedAt: now
    };
    
    // If status is 'new', change to 'read'
    if (message.status === 'new') {
      updates.status = 'read';
    }
    
    await saveToFirestore(COLLECTIONS.messages, messageId, updates);
  };

  // Mark message as read by client
  const markMessageReadByClient = async (messageId) => {
    const message = getMessageById(messageId);
    if (!message) return;
    
    const now = new Date().toISOString();
    await saveToFirestore(COLLECTIONS.messages, messageId, {
      lastClientReadAt: now,
      updatedAt: now
    });
  };

  // Assign message to a manager
  const assignMessage = async (messageId, employeeId) => {
    const employee = employeeId ? getEmployeeById(employeeId) : null;
    await saveToFirestore(COLLECTIONS.messages, messageId, {
      assignedTo: employeeId,
      assignedToName: employee ? `${employee.firstName} ${employee.lastName}`.trim() : null,
      updatedAt: new Date().toISOString()
    });
  };

  // Mark message as resolved
  const resolveMessage = async (messageId) => {
    await saveToFirestore(COLLECTIONS.messages, messageId, {
      status: 'resolved',
      resolvedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  };

  // Reconcile unmatched SMS messages — retroactively match phone numbers to clients
  const reconcilingRef = useRef(false);
  const reconcileUnmatchedMessages = useCallback(async () => {
    if (!state.initialized || state.clients.length === 0 || state.messages.length === 0) return;
    if (reconcilingRef.current) return;

    const unmatched = state.messages.filter(
      m => !m.clientId && m.source === 'sms' && m.clientPhone
    );
    if (unmatched.length === 0) return;

    // Build a lookup: normalized phone -> client
    const phoneToClient = {};
    for (const c of state.clients) {
      const digits = (c.phone || '').replace(/\D/g, '');
      const normalized = digits.length === 11 && digits.startsWith('1')
        ? digits.slice(1)
        : digits;
      if (normalized.length >= 10) {
        phoneToClient[normalized] = c;
      }
    }

    // Check if any unmatched messages can actually be matched before locking
    const toReconcile = unmatched.filter(msg => {
      const raw = (msg.clientPhone || '').replace(/\D/g, '');
      const normalized = raw.length === 11 && raw.startsWith('1')
        ? raw.slice(1)
        : raw;
      return !!phoneToClient[normalized];
    });
    if (toReconcile.length === 0) return;

    reconcilingRef.current = true;
    console.log(`Reconciling ${toReconcile.length} unmatched SMS message(s)...`);

    try {
      for (const msg of toReconcile) {
        const raw = (msg.clientPhone || '').replace(/\D/g, '');
        const normalized = raw.length === 11 && raw.startsWith('1')
          ? raw.slice(1)
          : raw;
        const client = phoneToClient[normalized];

        const clientName = `${client.firstName} ${client.lastName}`.trim();
        console.log(`  ${msg.id}: "${msg.clientName}" → "${clientName}"`);

        await saveToFirestore(COLLECTIONS.messages, msg.id, {
          clientId: client.id,
          clientName: clientName,
          clientEmail: client.email || msg.clientEmail || '',
          updatedAt: new Date().toISOString()
        });
      }
    } finally {
      reconcilingRef.current = false;
    }
  }, [state.initialized, state.clients, state.messages]);

  // Auto-run reconciliation when data is ready
  useEffect(() => {
    reconcileUnmatchedMessages();
  }, [reconcileUnmatchedMessages]);

  // ===== SHIFT OPERATIONS =====

  // Get shifts for a date range
  const getShiftsByDateRange = (startDate, endDate) => {
    return state.shifts.filter(s => s.date >= startDate && s.date <= endDate);
  };

  // Get shifts for a specific date
  const getShiftsByDate = (date) => {
    return state.shifts.filter(s => s.date === date);
  };

  // Get shifts for an employee
  const getEmployeeShifts = (employeeId, startDate = null, endDate = null) => {
    let shifts = state.shifts.filter(s => s.employeeId === employeeId);
    if (startDate) shifts = shifts.filter(s => s.date >= startDate);
    if (endDate) shifts = shifts.filter(s => s.date <= endDate);
    return shifts.sort((a, b) => a.date.localeCompare(b.date));
  };

  // Add a shift
  const addShift = async (shiftData) => {
    const shift = {
      ...shiftData,
      id: generateId('shift'),
      status: 'scheduled',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.shifts, shift.id, shift);
    return shift;
  };

  // Update a shift
  const updateShift = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    await saveToFirestore(COLLECTIONS.shifts, id, data);
  };

  // Delete a shift
  const deleteShift = async (id) => {
    await deleteFromFirestore(COLLECTIONS.shifts, id);
  };

  // Bulk create shifts (for generating week from template)
  const createShiftsFromTemplate = async (weekStartDate, employeeShifts) => {
    const shifts = [];
    for (const empShift of employeeShifts) {
      const shift = await addShift({
        employeeId: empShift.employeeId,
        date: empShift.date,
        startTime: empShift.startTime,
        endTime: empShift.endTime,
        routeId: empShift.routeId || null,
        routeName: empShift.routeName || null,
        notes: empShift.notes || ''
      });
      shifts.push(shift);
    }
    return shifts;
  };

  // Copy shifts from one week to another
  const copyWeekShifts = async (sourceWeekStart, targetWeekStart) => {
    const sourceEnd = format(addDays(parseISO(sourceWeekStart), 6), 'yyyy-MM-dd');
    const sourceShifts = getShiftsByDateRange(sourceWeekStart, sourceEnd);

    // Use calendar-day difference so a DST transition between the two weeks
    // can't push every copied shift onto the wrong day.
    const daysDiff = differenceInCalendarDays(parseISO(targetWeekStart), parseISO(sourceWeekStart));

    const newShifts = [];
    for (const shift of sourceShifts) {
      const newDate = format(addDays(parseISO(shift.date), daysDiff), 'yyyy-MM-dd');

      // Build the payload without any `undefined` fields. Shifts created via
      // the Add Shift modal have no routeId, so copying `routeId: undefined`
      // (or undefined routeName/notes) made Firestore's setDoc throw, which was
      // swallowed by saveToFirestore — the copy silently did nothing.
      const payload = {
        employeeId: shift.employeeId,
        date: newDate,
        startTime: shift.startTime,
        endTime: shift.endTime,
        routeName: shift.routeName ?? '',
        notes: shift.notes ?? ''
      };
      if (shift.routeId != null) payload.routeId = shift.routeId;

      const newShift = await addShift(payload);
      newShifts.push(newShift);
    }
    return newShifts;
  };

  // Check if shift is outside employee availability
  const isShiftOutsideAvailability = (employeeId, date, startTime, endTime) => {
    const employee = getEmployeeById(employeeId);
    if (!employee?.availability) return false;
    
    const dayOfWeek = format(parseISO(date), 'EEEE').toLowerCase();
    const availability = employee.availability[dayOfWeek];
    
    if (!availability || !availability.available) return true;
    
    // Check if shift times are within availability
    if (startTime < availability.start || endTime > availability.end) {
      return true;
    }
    
    return false;
  };

  // ===== TIME OFF REQUEST OPERATIONS =====

  // Get time off requests
  const getTimeOffRequests = (status = null) => {
    if (status) {
      return state.timeOffRequests.filter(t => t.status === status);
    }
    return state.timeOffRequests;
  };

  // Get time off for an employee
  const getEmployeeTimeOff = (employeeId) => {
    return state.timeOffRequests.filter(t => t.employeeId === employeeId);
  };

  // Check if employee has time off on a date
  const hasTimeOffOnDate = (employeeId, date) => {
    return state.timeOffRequests.some(t => 
      t.employeeId === employeeId && 
      t.status === 'approved' &&
      t.startDate <= date && 
      t.endDate >= date
    );
  };

  // Add time off request
  const addTimeOffRequest = async (requestData) => {
    const request = {
      ...requestData,
      id: generateId('pto'),
      status: 'pending',
      requestedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.timeOffRequests, request.id, request);
    return request;
  };

  // Check if the current user is the employee on a time off request
  const isOwnTimeOffRequest = (requestId) => {
    const request = state.timeOffRequests.find(t => t.id === requestId);
    if (!request) return false;
    const employee = getEmployeeById(request.employeeId);
    if (!employee || !user?.email) return false;
    return employee.email && employee.email.toLowerCase() === user.email.toLowerCase();
  };

  // Approve time off request
  const approveTimeOffRequest = async (id, managerNote = '') => {
    if (isOwnTimeOffRequest(id)) {
      throw new Error('You cannot approve your own time off request');
    }
    await saveToFirestore(COLLECTIONS.timeOffRequests, id, {
      status: 'approved',
      managerNote,
      reviewedAt: new Date().toISOString(),
      reviewedBy: user?.email || 'admin',
      updatedAt: new Date().toISOString()
    });
  };

  // Deny time off request
  const denyTimeOffRequest = async (id, managerNote = '') => {
    if (isOwnTimeOffRequest(id)) {
      throw new Error('You cannot deny your own time off request');
    }
    await saveToFirestore(COLLECTIONS.timeOffRequests, id, {
      status: 'denied',
      managerNote,
      reviewedAt: new Date().toISOString(),
      reviewedBy: user?.email || 'admin',
      updatedAt: new Date().toISOString()
    });
  };

  // Cancel time off request (by employee)
  const cancelTimeOffRequest = async (id) => {
    await saveToFirestore(COLLECTIONS.timeOffRequests, id, {
      status: 'cancelled',
      cancelledAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  };

  // ===== INTERNAL MESSAGE OPERATIONS =====

  // Get internal messages for an employee
  const getEmployeeInternalMessages = (employeeId) => {
    return state.internalMessages.filter(m => 
      m.toId === employeeId || 
      m.toId === 'all' || 
      (m.toType === 'team' && m.toId === getEmployeeById(employeeId)?.teamId)
    ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  // Get announcements (messages to all)
  const getAnnouncements = () => {
    return state.internalMessages
      .filter(m => m.type === 'announcement')
      .sort((a, b) => {
        // Pinned first, then by date
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
  };

  // Get unread count for employee
  const getEmployeeUnreadInternalCount = (employeeId) => {
    const messages = getEmployeeInternalMessages(employeeId);
    return messages.filter(m => !m.readBy?.includes(employeeId)).length;
  };

  // Create internal message/announcement
  const createInternalMessage = async (messageData) => {
    const message = {
      ...messageData,
      id: generateId('imsg'),
      readBy: [],
      acknowledgedBy: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.internalMessages, message.id, message);
    return message;
  };

  // Create announcement
  const createAnnouncement = async (data) => {
    const announcement = await createInternalMessage({
      type: 'announcement',
      toId: 'all',
      toType: 'all',
      fromId: user?.uid || 'admin',
      fromName: 'Management',
      subject: data.subject,
      body: data.body,
      isPinned: data.isPinned || false,
      requiresAcknowledgment: data.requiresAcknowledgment || false,
      expiresAt: data.expiresAt || null,
      sendEmail: data.sendEmail || false
    });
    return announcement;
  };

  // Create direct message to employee
  const createDirectMessage = async (toEmployeeId, data) => {
    const toEmployee = getEmployeeById(toEmployeeId);
    const message = await createInternalMessage({
      type: 'direct',
      toId: toEmployeeId,
      toType: 'employee',
      toName: toEmployee ? `${toEmployee.firstName} ${toEmployee.lastName || ''}`.trim() : 'Unknown',
      fromId: user?.uid || 'admin',
      fromName: data.fromName || 'Management',
      subject: data.subject,
      body: data.body,
      isPinned: false,
      requiresAcknowledgment: false
    });
    return message;
  };

  // Create schedule update message
  const createScheduleUpdateMessage = async (employeeId, data) => {
    const message = await createInternalMessage({
      type: 'schedule_update',
      toId: employeeId,
      toType: 'employee',
      fromId: 'system',
      fromName: 'System',
      subject: data.subject || 'Schedule Update',
      body: data.body,
      isPinned: false,
      requiresAcknowledgment: false
    });
    return message;
  };

  // Mark message as read by employee
  const markInternalMessageRead = async (messageId, employeeId) => {
    const message = state.internalMessages.find(m => m.id === messageId);
    if (!message) return;
    
    const readBy = message.readBy || [];
    if (!readBy.includes(employeeId)) {
      await saveToFirestore(COLLECTIONS.internalMessages, messageId, {
        readBy: [...readBy, employeeId],
        updatedAt: new Date().toISOString()
      });
    }
  };

  // Acknowledge message
  const acknowledgeInternalMessage = async (messageId, employeeId) => {
    const message = state.internalMessages.find(m => m.id === messageId);
    if (!message) return;
    
    const acknowledgedBy = message.acknowledgedBy || [];
    const readBy = message.readBy || [];
    
    const updates = { updatedAt: new Date().toISOString() };
    
    if (!acknowledgedBy.includes(employeeId)) {
      updates.acknowledgedBy = [...acknowledgedBy, employeeId];
    }
    if (!readBy.includes(employeeId)) {
      updates.readBy = [...readBy, employeeId];
    }
    
    await saveToFirestore(COLLECTIONS.internalMessages, messageId, updates);
  };

  // Update internal message (for editing announcements)
  const updateInternalMessage = async (id, updates) => {
    await saveToFirestore(COLLECTIONS.internalMessages, id, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  };

  // Delete internal message
  const deleteInternalMessage = async (id) => {
    await deleteFromFirestore(COLLECTIONS.internalMessages, id);
  };

  // Reply to internal message
  const replyToInternalMessage = async (originalMessageId, replyData) => {
    const original = state.internalMessages.find(m => m.id === originalMessageId);
    if (!original) return;

    // Swap from/to for reply
    const reply = await createInternalMessage({
      type: 'direct',
      toId: original.fromId,
      toType: original.fromId === 'system' ? 'system' : 'employee',
      toName: original.fromName,
      fromId: replyData.fromId,
      fromName: replyData.fromName,
      subject: `Re: ${original.subject}`,
      body: replyData.body,
      replyToId: originalMessageId,
      isPinned: false,
      requiresAcknowledgment: false
    });
    return reply;
  };

  // ===== TODO ACTIONS =====

  const addTodo = async (todoData) => {
    const now = new Date().toISOString();
    const todo = {
      id: generateId('todo_'),
      text: todoData.text || '',
      priority: todoData.priority || 'medium',
      category: todoData.category || 'ops',
      done: false,
      dueDate: todoData.dueDate || null,
      relatedId: todoData.relatedId || null,
      relatedType: todoData.relatedType || null,
      createdAt: now,
      updatedAt: now,
      createdBy: user?.email || 'admin'
    };
    dispatch({ type: 'ADD_TODO', payload: todo });
    await saveToFirestore(COLLECTIONS.todos, todo.id, todo);
    return todo;
  };

  const updateTodo = async (id, updates) => {
    const now = new Date().toISOString();
    const data = { ...updates, updatedAt: now };
    dispatch({ type: 'UPDATE_TODO', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.todos, id, data);
  };

  const toggleTodo = async (id) => {
    const todo = state.todos.find(t => t.id === id);
    if (!todo) return;
    await updateTodo(id, { done: !todo.done });
  };

  const deleteTodo = async (id) => {
    dispatch({ type: 'DELETE_TODO', payload: id });
    await deleteFromFirestore(COLLECTIONS.todos, id);
  };

  // ===== DAY NOTES =====
  // Free-form date-level notes shown on Dispatch (e.g. "Ashanti leaves 3pm",
  // "Move the Hendersons", supply runs). Each note is its own doc keyed by id
  // with a `date` field (YYYY-MM-DD) so we can filter by day.
  const getDayNotes = useCallback((date) => {
    return (state.dayNotes || [])
      .filter(n => n.date === date)
      .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
  }, [state.dayNotes]);

  const addDayNote = async ({ date, text, type = 'general' }) => {
    const now = new Date().toISOString();
    const note = {
      id: generateId('note_'),
      date,
      text: text || '',
      type, // 'general' | 'pin' — pin shows as a highlighted alert
      createdAt: now,
      updatedAt: now,
      createdBy: user?.email || 'admin'
    };
    dispatch({ type: 'ADD_DAY_NOTE', payload: note });
    await saveToFirestore(COLLECTIONS.dayNotes, note.id, note);
    return note;
  };

  const updateDayNote = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE_DAY_NOTE', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.dayNotes, id, data);
  };

  const deleteDayNote = async (id) => {
    dispatch({ type: 'DELETE_DAY_NOTE', payload: id });
    await deleteFromFirestore(COLLECTIONS.dayNotes, id);
  };

  // ===== REVIEWS =====
  const createReview = async (employeeId) => {
    const now = new Date().toISOString();
    const employee = getEmployeeById(employeeId);
    const review = {
      id: generateId('rev_'),
      employeeId,
      employeeName: employee ? `${employee.firstName} ${employee.lastName}`.trim() : '',
      reviewType: '90_day',
      status: 'draft', // draft → pending_employee → pending_admin → completed
      createdAt: now,
      updatedAt: now,
      createdBy: user?.email || 'admin',
      // Employee self-review
      employeeScores: null,
      employeeStrengths: '',
      employeeGrowth: '',
      employeeGoals: '',
      employeeComments: '',
      employeeCompletedAt: null,
      // Admin review
      adminScores: null,
      adminStrengths: '',
      adminGrowth: '',
      adminGoals: '',
      adminComments: '',
      adminCompletedAt: null,
      // Discussion
      discussionDate: null,
      discussionNotes: '',
      discussionActionItems: '',
      completedAt: null
    };
    dispatch({ type: 'ADD_REVIEW', payload: review });
    await saveToFirestore(COLLECTIONS.reviews, review.id, review);
    return review;
  };

  const updateReview = async (id, updates) => {
    const now = new Date().toISOString();
    const data = { ...updates, updatedAt: now };
    dispatch({ type: 'UPDATE_REVIEW', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.reviews, id, data);
  };

  const getEmployeeReviews = (employeeId) => {
    return state.reviews
      .filter(r => r.employeeId === employeeId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  const getReviewById = (id) => {
    return state.reviews.find(r => r.id === id);
  };

  const getPendingEmployeeReviews = (employeeId) => {
    return state.reviews.filter(r => r.employeeId === employeeId && r.status === 'pending_employee');
  };

  // Get managers for assignment dropdown
  const getManagers = () => {
    return state.employees.filter(e =>
      e.role === 'manager' || e.role === 'admin' || e.role === 'lead'
    );
  };

  // ===== QUIZ OPERATIONS =====
  const addQuiz = async (quizData) => {
    const quiz = {
      ...quizData,
      id: quizData.id || generateId('quiz'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: quizData.status || 'active'
    };
    await saveToFirestore(COLLECTIONS.quizzes, quiz.id, quiz);
    return quiz;
  };

  const updateQuiz = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE_QUIZ', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.quizzes, id, data);
  };

  const deleteQuiz = async (id) => {
    await deleteFromFirestore(COLLECTIONS.quizzes, id);
  };

  const getQuizById = (id) => state.quizzes.find(q => q.id === id);

  const submitQuizAttempt = async (attemptData) => {
    const attempt = {
      ...attemptData,
      id: generateId('qatt'),
      submittedAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.quizAttempts, attempt.id, attempt);
    return attempt;
  };

  const getEmployeeQuizAttempts = (employeeId) => {
    return state.quizAttempts
      .filter(a => a.employeeId === employeeId)
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  };

  const getQuizAttemptsByMonth = (employeeId, year, month) => {
    return state.quizAttempts.filter(a => {
      if (a.employeeId !== employeeId) return false;
      const d = new Date(a.submittedAt);
      return d.getFullYear() === year && d.getMonth() === month;
    });
  };

  // ===== EMPLOYEE DOCUMENT ACTIONS =====

  const addEmployeeDocument = async (docData) => {
    const document = {
      ...docData,
      id: generateId('edoc'),
      uploadedAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.employeeDocuments, document.id, document);
    return document;
  };

  const deleteEmployeeDocument = async (id) => {
    await deleteFromFirestore(COLLECTIONS.employeeDocuments, id);
  };

  const getEmployeeDocuments = (employeeId) => {
    return state.employeeDocuments
      .filter(d => d.employeeId === employeeId)
      .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
  };

  // ===== CLIENT DOCUMENT ACTIONS =====

  const addClientDocument = async (docData) => {
    const document = {
      ...docData,
      id: generateId('cdoc'),
      uploadedAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.clientDocuments, document.id, document);
    return document;
  };

  const deleteClientDocument = async (id) => {
    await deleteFromFirestore(COLLECTIONS.clientDocuments, id);
  };

  const getClientDocuments = (clientId) => {
    return state.clientDocuments
      .filter(d => d.clientId === clientId)
      .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
  };

  // ===== EMPLOYEE CONVERSATION ACTIONS =====

  const addEmployeeConversation = async (convData) => {
    const conversation = {
      ...convData,
      id: generateId('conv'),
      createdAt: new Date().toISOString()
    };
    await saveToFirestore(COLLECTIONS.employeeConversations, conversation.id, conversation);
    return conversation;
  };

  const updateEmployeeConversation = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    await saveToFirestore(COLLECTIONS.employeeConversations, id, data);
  };

  const deleteEmployeeConversation = async (id) => {
    await deleteFromFirestore(COLLECTIONS.employeeConversations, id);
  };

  const getEmployeeConversations = (employeeId) => {
    return state.employeeConversations
      .filter(c => c.employeeId === employeeId)
      .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
  };

  // ===== ATTENDANCE POINTS =====

  const addAttendancePoint = async (pointData) => {
    const now = new Date().toISOString();
    const employee = getEmployeeById(pointData.employeeId);
    const point = {
      ...pointData,
      id: pointData.id || generateId('apt_'),
      employeeName: employee ? `${employee.firstName} ${employee.lastName}`.trim() : '',
      createdAt: now,
      updatedAt: now,
      createdBy: pointData.createdBy || user?.email || 'system',
      voided: false,
    };
    dispatch({ type: 'ADD_ATTENDANCEPOINT', payload: point });
    await saveToFirestore(COLLECTIONS.attendancePoints, point.id, point);
    return point;
  };

  const updateAttendancePoint = async (id, updates) => {
    const now = new Date().toISOString();
    const data = { ...updates, updatedAt: now };
    dispatch({ type: 'UPDATE_ATTENDANCEPOINT', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.attendancePoints, id, data);
  };

  const voidAttendancePoint = async (id, reason) => {
    await updateAttendancePoint(id, {
      voided: true,
      voidedAt: new Date().toISOString(),
      voidedBy: user?.email || 'admin',
      voidReason: reason || '',
    });
  };

  const deleteAttendancePoint = async (id) => {
    dispatch({ type: 'DELETE_ATTENDANCEPOINT', payload: id });
    await deleteFromFirestore(COLLECTIONS.attendancePoints, id);
  };

  const getEmployeeAttendancePoints = (employeeId) => {
    return state.attendancePoints
      .filter(p => p.employeeId === employeeId)
      .sort((a, b) => (b.date || b.createdAt || '').localeCompare(a.date || a.createdAt || ''));
  };

  // ===== PTO / EMPLOYMENT STATUS =====
  // Full-time: 35+ hrs/week average
  const FULL_TIME_HOURS = 35;

  const getEmployeeAvgWeeklyHours = (employeeId, weeksBack = 8) => {
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - (weeksBack * 7));
    const cutoffStr = format(cutoff, 'yyyy-MM-dd');

    const completedJobs = state.jobs.filter(j =>
      j.team?.includes(employeeId) &&
      j.date >= cutoffStr &&
      (j.status === 'completed' || j.clockOutTime)
    );

    let totalHours = 0;
    completedJobs.forEach(j => {
      if (j.duration) {
        totalHours += j.duration;
      } else if (j.clockInTime && j.clockOutTime) {
        const diff = (new Date(j.clockOutTime) - new Date(j.clockInTime)) / 3600000;
        totalHours += Math.max(0, diff);
      }
    });

    const avgWeekly = weeksBack > 0 ? totalHours / weeksBack : 0;
    return Math.round(avgWeekly * 10) / 10;
  };

  const getEmploymentStatus = (employeeId) => {
    const avgHours = getEmployeeAvgWeeklyHours(employeeId);
    const isFullTime = avgHours >= FULL_TIME_HOURS;
    return {
      avgWeeklyHours: avgHours,
      status: isFullTime ? 'full_time' : 'part_time',
      ptoEligible: isFullTime,
      threshold: FULL_TIME_HOURS,
    };
  };

  // ===== COMPLAINT ACTIONS =====
  const addComplaint = async (complaintData) => {
    const complaint = {
      ...complaintData,
      id: generateId('comp'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: complaintData.status || 'open'
    };
    dispatch({ type: 'ADD_COMPLAINT', payload: complaint });
    await saveToFirestore(COLLECTIONS.complaints, complaint.id, complaint);
    return complaint;
  };

  const updateComplaint = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE_COMPLAINT', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.complaints, id, data);
  };

  const deleteComplaint = async (id) => {
    dispatch({ type: 'DELETE_COMPLAINT', payload: id });
    await deleteFromFirestore(COLLECTIONS.complaints, id);
  };

  // ===== FEEDBACK ACTIONS =====
  const addFeedback = async (feedbackData) => {
    const feedback = {
      ...feedbackData,
      id: generateId('fb'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    dispatch({ type: 'ADD_FEEDBACK', payload: feedback });
    await saveToFirestore(COLLECTIONS.feedbacks, feedback.id, feedback);
    return feedback;
  };

  const updateFeedback = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE_FEEDBACK', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.feedbacks, id, data);
  };

  const deleteFeedback = async (id) => {
    dispatch({ type: 'DELETE_FEEDBACK', payload: id });
    await deleteFromFirestore(COLLECTIONS.feedbacks, id);
  };

  // ===== EXPENSE ACTIONS =====
  const addExpense = async (expenseData) => {
    const expense = {
      ...expenseData,
      id: generateId('exp'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    dispatch({ type: 'ADD_EXPENSE', payload: expense });
    await saveToFirestore(COLLECTIONS.expenses, expense.id, expense);
    return expense;
  };

  const updateExpense = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE_EXPENSE', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.expenses, id, data);
  };

  const deleteExpense = async (id) => {
    dispatch({ type: 'DELETE_EXPENSE', payload: id });
    await deleteFromFirestore(COLLECTIONS.expenses, id);
  };

  // ===== DEPOSIT ACTIONS =====
  // Optimistic updates are rolled back on Firestore failure, otherwise the
  // real-time onSnapshot listener later overwrites local state and the
  // deposit silently disappears from the UI.
  const addDeposit = async (depositData) => {
    const deposit = {
      ...depositData,
      id: generateId('dep'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    dispatch({ type: 'ADD_DEPOSIT', payload: deposit });
    const ok = await saveToFirestore(COLLECTIONS.deposits, deposit.id, deposit);
    if (!ok) {
      dispatch({ type: 'DELETE_DEPOSIT', payload: deposit.id });
      throw new Error('Failed to save deposit. Please check your connection and try again.');
    }
    return deposit;
  };

  const updateDeposit = async (id, updates) => {
    const original = state.deposits.find(d => d.id === id);
    const data = { ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE_DEPOSIT', payload: { id, ...data } });
    const ok = await saveToFirestore(COLLECTIONS.deposits, id, data);
    if (!ok) {
      if (original) {
        dispatch({ type: 'UPDATE_DEPOSIT', payload: original });
      }
      throw new Error('Failed to update deposit. Please check your connection and try again.');
    }
  };

  const deleteDeposit = async (id) => {
    const original = state.deposits.find(d => d.id === id);
    dispatch({ type: 'DELETE_DEPOSIT', payload: id });
    const ok = await deleteFromFirestore(COLLECTIONS.deposits, id);
    if (!ok) {
      if (original) {
        dispatch({ type: 'ADD_DEPOSIT', payload: original });
      }
      throw new Error('Failed to delete deposit. Please check your connection and try again.');
    }
  };

  // ===== ACCOUNT ACTIONS =====
  const addAccount = async (accountData) => {
    const account = {
      ...accountData,
      id: generateId('acct'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    dispatch({ type: 'ADD_ACCOUNT', payload: account });
    await saveToFirestore(COLLECTIONS.accounts, account.id, account);
    return account;
  };

  const updateAccount = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE_ACCOUNT', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.accounts, id, data);
  };

  const deleteAccount = async (id) => {
    dispatch({ type: 'DELETE_ACCOUNT', payload: id });
    await deleteFromFirestore(COLLECTIONS.accounts, id);
  };

  // ===== EXPENSE CATEGORY ACTIONS =====
  const addExpenseCategory = async (categoryData) => {
    const category = {
      ...categoryData,
      id: generateId('cat'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    dispatch({ type: 'ADD_EXPENSECATEGORY', payload: category });
    await saveToFirestore(COLLECTIONS.expenseCategories, category.id, category);
    return category;
  };

  const updateExpenseCategory = async (id, updates) => {
    const data = { ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE_EXPENSECATEGORY', payload: { id, ...data } });
    await saveToFirestore(COLLECTIONS.expenseCategories, id, data);
  };

  const deleteExpenseCategory = async (id) => {
    dispatch({ type: 'DELETE_EXPENSECATEGORY', payload: id });
    await deleteFromFirestore(COLLECTIONS.expenseCategories, id);
  };

  // ===== TRANSFER ACTIONS =====
  // Transfers move money between two of the user's own accounts. They are
  // intentionally stored in their own collection (not as paired deposit +
  // expense) so they don't pollute income or expense totals / P&L.
  const addTransfer = async (transferData) => {
    if (!transferData.fromAccountId || !transferData.toAccountId) {
      throw new Error('Transfer requires a source and destination account.');
    }
    if (transferData.fromAccountId === transferData.toAccountId) {
      throw new Error('Source and destination accounts must be different.');
    }
    const transfer = {
      ...transferData,
      amount: Number(transferData.amount) || 0,
      id: generateId('xfer'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    dispatch({ type: 'ADD_TRANSFER', payload: transfer });
    const ok = await saveToFirestore(COLLECTIONS.transfers, transfer.id, transfer);
    if (!ok) {
      dispatch({ type: 'DELETE_TRANSFER', payload: transfer.id });
      throw new Error('Failed to save transfer. Please check your connection and try again.');
    }
    return transfer;
  };

  const updateTransfer = async (id, updates) => {
    const original = state.transfers.find(t => t.id === id);
    if (updates.fromAccountId && updates.toAccountId &&
        updates.fromAccountId === updates.toAccountId) {
      throw new Error('Source and destination accounts must be different.');
    }
    const data = { ...updates, updatedAt: new Date().toISOString() };
    if (data.amount !== undefined) data.amount = Number(data.amount) || 0;
    dispatch({ type: 'UPDATE_TRANSFER', payload: { id, ...data } });
    const ok = await saveToFirestore(COLLECTIONS.transfers, id, data);
    if (!ok) {
      if (original) dispatch({ type: 'UPDATE_TRANSFER', payload: original });
      throw new Error('Failed to update transfer. Please check your connection and try again.');
    }
  };

  const deleteTransfer = async (id) => {
    const original = state.transfers.find(t => t.id === id);
    dispatch({ type: 'DELETE_TRANSFER', payload: id });
    const ok = await deleteFromFirestore(COLLECTIONS.transfers, id);
    if (!ok) {
      if (original) dispatch({ type: 'ADD_TRANSFER', payload: original });
      throw new Error('Failed to delete transfer. Please check your connection and try again.');
    }
  };

  const value = {
    state,
    dispatch,

    // Getters
    getClientById,
    getEmployeeById,
    getPropertyById,
    getClientProperties,
    getEstimateById,
    getClientEstimates,
    getPropertyEstimates,
    getEstimateByToken,
    getPendingEstimates,
    getEstimatesRequiringReview,
    getJobById,
    getClientJobs,
    getPropertyJobs,
    getJobsByStatus,
    getJobsByDate,
    getInvoiceById,
    getClientInvoices,
    getJobInvoice,
    getMessageById,
    getClientMessages,
    getAdminUnreadCount,
    getClientUnreadCount,
    getManagers,
    
    // CRUD Actions
    addClient,
    updateClient,
    logClientCommunication,
    deleteClient,
    setMasterPassword,
    verifyMasterPassword,
    hasMasterPassword,
    findDuplicateClients,
    findOrCreateClient,
    mergeClients,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    addProperty,
    updateProperty,
    logDispatchActivity,
    addDispatchComment,
    createEstimate,
    updateEstimate,
    deleteEstimate,
    sendEstimate,
    renewEstimate,
    approveEstimate,
    addJob,
    updateJob,
    deleteJob,
    updateJobChecklistItem,
    deleteSeriesJobs,
    deleteProperty,
    mergeProperties,
    convertEstimateToJob,
    createInvoice,
    createInvoiceForJob,
    addInvoice,
    updateInvoice,
    deleteInvoice,
    
    // Message Actions
    addMessage,
    addReply,
    updateMessage,
    markMessageReadByAdmin,
    markMessageReadByClient,
    assignMessage,
    resolveMessage,
    reconcileUnmatchedMessages,

    // Shift Actions
    getShiftsByDateRange,
    getShiftsByDate,
    getEmployeeShifts,
    addShift,
    updateShift,
    deleteShift,
    createShiftsFromTemplate,
    copyWeekShifts,
    isShiftOutsideAvailability,

    // Time Off Actions
    getTimeOffRequests,
    getEmployeeTimeOff,
    hasTimeOffOnDate,
    addTimeOffRequest,
    approveTimeOffRequest,
    denyTimeOffRequest,
    cancelTimeOffRequest,
    isOwnTimeOffRequest,

    // Internal Message Actions
    getEmployeeInternalMessages,
    getAnnouncements,
    getEmployeeUnreadInternalCount,
    createInternalMessage,
    createAnnouncement,
    createDirectMessage,
    createScheduleUpdateMessage,
    markInternalMessageRead,
    acknowledgeInternalMessage,
    updateInternalMessage,
    deleteInternalMessage,
    replyToInternalMessage,
    
    // Utilities
    generateId,
    
    // Todo Actions
    addTodo,
    updateTodo,
    toggleTodo,
    deleteTodo,

    // Day Note Actions
    getDayNotes,
    addDayNote,
    updateDayNote,
    deleteDayNote,

    // Review Actions
    createReview,
    updateReview,
    getEmployeeReviews,
    getReviewById,
    getPendingEmployeeReviews,
    
    // Communication Log Actions
    addCommunicationLog,
    deleteCommunicationLog,

    // Quiz Actions
    addQuiz,
    updateQuiz,
    deleteQuiz,
    getQuizById,
    submitQuizAttempt,
    getEmployeeQuizAttempts,
    getQuizAttemptsByMonth,

    // Employee Document Actions
    addEmployeeDocument,
    deleteEmployeeDocument,
    getEmployeeDocuments,

    // Client Document Actions
    addClientDocument,
    deleteClientDocument,
    getClientDocuments,

    // Employee Conversation Actions
    addEmployeeConversation,
    updateEmployeeConversation,
    deleteEmployeeConversation,
    getEmployeeConversations,

    // Attendance Point Actions
    addAttendancePoint,
    updateAttendancePoint,
    voidAttendancePoint,
    deleteAttendancePoint,
    getEmployeeAttendancePoints,

    // PTO / Employment Status
    getEmployeeAvgWeeklyHours,
    getEmploymentStatus,

    // Complaint Actions
    addComplaint,
    updateComplaint,
    deleteComplaint,

    // Feedback Actions
    addFeedback,
    updateFeedback,
    deleteFeedback,

    // Expense Actions
    addExpense,
    updateExpense,
    deleteExpense,

    // Deposit Actions
    addDeposit,
    updateDeposit,
    deleteDeposit,

    // Account Actions (bank / cash / credit accounts)
    addAccount,
    updateAccount,
    deleteAccount,

    // Expense Category Actions
    addExpenseCategory,
    updateExpenseCategory,
    deleteExpenseCategory,

    // Transfer Actions (between own accounts)
    addTransfer,
    updateTransfer,
    deleteTransfer,

    // Database management
    seedDatabase,
    clearDatabase,
    backfillSalesAnalyticsFields,
    backfillSalesMetrics,
    loadAllData
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// Hook
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
